import { html, nothing } from "lit";
import "../components/fleet-assist-draft.ts";
import "../components/fleet-popover.ts";
import "../components/fleet-toggle.ts";
import { ref } from "lit/directives/ref.js";
import { repeat } from "lit/directives/repeat.js";
import type { EventLogEntry } from "../app-events.ts";
import { buildChatItems } from "../chat/build-chat-items.ts";
import { resolveLiveThinkingSummary } from "../chat/chat-activity-collect.ts";
import { resolveAssistDisplay, type ChatAssistDisplay } from "../chat/chat-assist-display.ts";
import { renderAssistantTurnGroup, type AssistantTurn } from "../chat/chat-assistant-turn.ts";
import { classifyFile } from "../chat/chat-attachment-classifier.ts";
import { renderChatComposerFileReview } from "../chat/chat-composer-file-review.ts";
import {
  renderComposerLiveStep,
  resolveComposerLiveStep,
} from "../chat/chat-composer-live-step.ts";
import { renderChatComposerPlusMenu } from "../chat/chat-composer-plus-menu.ts";
import { resolveContextUsage } from "../chat/chat-context-usage.ts";
import { parseDisplayChatError } from "../chat/chat-errors.ts";
import {
  renderChatFileChangesSummary,
  type SessionArtifactFileSummary,
  type SessionArtifactSummaryTotals,
} from "../chat/chat-file-changes-summary.ts";
import { renderChatFileDiffPanel } from "../chat/chat-file-diff-viewer.ts";
import { renderChatGuardrailBanner } from "../chat/chat-guardrail-banner.ts";
import type { ChatHistoryEntry, ChatHistoryGroup } from "../chat/chat-history-catalog.ts";
import { resolveChatHistoryPaging } from "../chat/chat-history-paging.ts";
import {
  buildLiveRunSteps,
  buildLiveRunStepsWithSticky,
  hasActiveToolInStream,
  renderChatLiveRunGroup,
} from "../chat/chat-live-run.ts";
import { renderNetworkBlipToast } from "../chat/chat-network-blip-toast.ts";
import { renderChatPinnedQuestion } from "../chat/chat-pinned-question.ts";
import { renderPlanImplementOffer } from "../chat/chat-plan-implement-offer.ts";
import { renderRunControlBar } from "../chat/chat-run-control-bar.ts";
import { sessionChildren } from "../chat/chat-session-children.ts";
import { renderChatSyncPill } from "../chat/chat-sync-pill.ts";
import { buildChatTabLabelHints } from "../chat/chat-tab-labels.ts";
import { resolveChatHeartbeatActive } from "../chat/chat-transcript-filters.ts";
import {
  renderChatTranscriptLoadingOverlay,
  renderChatTranscriptProgressHairline,
  renderChatTranscriptSkeleton,
  shouldShowChatTranscriptOverlay,
  shouldShowTranscriptProgressHairline,
  shouldShowTranscriptSkeleton,
} from "../chat/chat-transcript-overlay.ts";
import {
  formatChatWaitElapsed,
  isChatWaitActive,
  shouldShowLiveRunIndicator,
} from "../chat/chat-wait-elapsed.ts";
import {
  applyComposerLocalSlashCommand,
  applyComposerLocalSlashFromDraft,
  listComposerSlashCommands,
  shouldSendAfterSlashPick,
} from "../chat/composer-local-slash.ts";
import { composerBlockCalloutClass, type ComposerBlock } from "../chat/composer-reason-channel.ts";
import { renderContextUsageRing } from "../chat/context-usage-ring.ts";
import type { GatewayModelChoice } from "../chat/delegate-router.ts";
import {
  extractFileContextQuery,
  extractFolderContextQuery,
  filterWorkspacePaths,
  filterFolderPaths,
  renderFileContextPicker,
  renderFolderContextPicker,
} from "../chat/file-context-picker.ts";
import { messageTextContent } from "../chat/fleet-chat-send-options.ts";
import type { FleetComposerMode } from "../chat/fleet-composer-mode.ts";
import { projectsForChatContext } from "../chat/fleet-projects-session.ts";
import {
  renderFleetRunActivityStrip,
  unitDetailsFromFleetTodos,
} from "../chat/fleet-run-activity-strip.ts";
import { selfImprovementWorkspaceHint } from "../chat/fleet-self-improvement-workspace.ts";
import {
  previewFleetSendRouting,
  renderFleetSendAckBanner,
  renderFleetSendRoutingPreview,
} from "../chat/fleet-send-routing-preview.ts";
import {
  extractSlashCommandQuery,
  filterSlashCommands,
  formatSlashCommandDraft,
} from "../chat/fleet-slash-commands.ts";
import { renderFleetTodosStrip } from "../chat/fleet-todos-strip.ts";
import {
  renderMessageGroup,
  renderReadingIndicatorGroup,
  renderStreamingGroup,
} from "../chat/grouped-render.ts";
import { extractMentionQuery, filterAgents, renderMentionPicker } from "../chat/mention-picker.ts";
import { extractThinking } from "../chat/message-extract.ts";
import {
  resolveMessageRewindTarget,
  type MessageRewindTarget,
} from "../chat/message-transcript.ts";
import { detectMultiAgentIntent, renderMultiAgentHint } from "../chat/multi-agent-hint.ts";
import { renderOrchestrationGroup } from "../chat/orchestration-group.ts";
import { resolveAgentIdForSession } from "../chat/resolve-session-agent.ts";
import { computeSessionLiveness } from "../chat/session-liveness.ts";
import type { SessionSnapshot } from "../chat/session-snapshot.ts";
import type { SessionTabActivityMap } from "../chat/session-tab-activity.ts";
import { renderSlashCommandPicker } from "../chat/slash-command-picker.ts";
import { isSubagentRunCard, resolveSubagentParentContext } from "../chat/subagent-run-display.ts";
import { renderSubagentRunRow } from "../chat/subagent-run-row.ts";
import { resolveSubagentActivityPhase } from "../chat/subagent-run-status.ts";
import { renderSubagentSessionWorkBanner } from "../chat/subagent-session-work-banner.ts";
import { FLEET_SEND_OPTIONS_HINTS } from "../control-hints.ts";
import { FLEET_CHAT_EDIT_HINTS, FLEET_CHAT_FOCUS_HINTS } from "../control-hints.ts";
import type { ChatState } from "../controllers/chat.ts";
import type {
  FleetQuestionLastAnswer,
  FleetQuestionRequest,
} from "../controllers/fleet-question.ts";
import { formatFleetAnswerEchoValue } from "../controllers/fleet-question.ts";
import type { FleetTodoItem } from "../controllers/fleet-todos.ts";
import type { RunArtifactPreviewEntry } from "../controllers/run-artifact-previews.ts";
import type {
  SessionArtifactDetail,
  SessionArtifactRecord,
} from "../controllers/session-artifacts.ts";
import type { GatewayHelloOk } from "../gateway.ts";
import { icons } from "../icons.ts";
import { renderMarkdownContent } from "../markdown-render.ts";
import type { OperatorTrustSessionState } from "../operator-trust/state.ts";
import type {
  FleetProjectSummary,
  GatewayAgentRow,
  AgentsListResult,
  QueueServiceStatus,
  SessionsListResult,
} from "../types.ts";
import type { ChatItem, MessageGroup } from "../types/chat-types.ts";
import type { AgentRosterItem } from "../ui-types.ts";
import type { ChatAttachment, ChatQueueItem } from "../ui-types.ts";
import { type ChatActivityTab, renderChatActivityPanel } from "../views/chat-activity-panel.ts";
import { renderChatCheckpointsPanel } from "../views/chat-checkpoints-panel.ts";
import { renderChatComposerToolbar } from "../views/chat-composer-toolbar.ts";
import {
  renderChatContextBreakdownPanel,
  type ChatContextBreakdownPanelProps,
} from "../views/chat-context-breakdown-panel.ts";
import { renderChatEnvironmentPanel } from "../views/chat-environment-panel.ts";
import { renderChatPalaceContinuityDropdown } from "../views/chat-palace-continuity-dropdown.ts";
import { renderChatRunTimeline } from "../views/chat-run-timeline.ts";
import type { ChatSessionContextMeta } from "../views/chat-session-context.ts";
import {
  renderFleetChatHistorySidebarMemo as renderFleetChatHistorySidebar,
  type FleetChatHistorySidebarProps,
} from "../views/fleet-chat-history-sidebar.ts";
import type { FleetChatMainTabId } from "../views/fleet-chat-main-tabs.ts";
import { renderFleetCollapsibleSection } from "../views/fleet-collapsible-section.ts";
import {
  buildFleetExecutionView,
  renderFleetExecutionPipeline,
} from "../views/fleet-execution-pipeline.ts";
import { renderFleetInspectorPanel } from "../views/fleet-inspector-panel.ts";
import { renderInspectorWidthPicker } from "../views/fleet-inspector-width-picker.ts";
import { renderFleetProjectFilesTab } from "../views/fleet-project-files-tab.ts";
import { renderFleetProjectsPanelBody } from "../views/fleet-projects-panel.ts";
import { renderFleetQuestionInline } from "../views/fleet-question-inline.ts";
import { renderFleetRunContextSummary } from "../views/fleet-run-context-summary.ts";
import { renderFleetSessionInspectorFilesTab } from "../views/fleet-session-inspector-files-tab.ts";
import { renderFleetSubagentActivityPanel } from "../views/fleet-subagent-activity-panel.ts";
import {
  renderFleetUnifiedLayout,
  type FleetUnifiedDrawerTab,
} from "../views/fleet-unified-layout.ts";
import {
  renderFleetV2AgentDirectory,
  type FleetV2DirectoryTab,
} from "../views/fleet-v2-agent-directory.ts";
import {
  renderFleetV2AnalyticsPanel,
  type FleetAnalyticsEventScope,
  type FleetV2AnalyticsTab,
} from "../views/fleet-v2-analytics-panel.ts";
import {
  applyFleetColumnResize,
  applyFleetRowResize,
  renderFleetWorkspaceLayout,
} from "../views/fleet-workspace-layout.ts";
import type {
  FleetInspectorTabId,
  FleetWorkspaceDensity,
  FleetWorkspaceLayoutState,
  FleetWorkspacePreset,
} from "../views/fleet-workspace-presets.ts";
import { renderFleetWorkspaceResizer } from "../views/fleet-workspace-resizer.ts";
import type { LocalLlmViewProps } from "../views/local-llm.ts";
import { renderLocalLlm } from "../views/local-llm.ts";
import { renderMarkdownSidebar } from "../views/markdown-sidebar.ts";
import { renderQueue } from "../views/queue.ts";
import { renderTunnelHealthPanel } from "../views/tunnel-health-panel.ts";
import {
  handleAttachInputChange,
  handleDragEnter,
  handleDragLeave,
  handleDragOver,
  handleDrop,
  handlePaste,
  renderAttachmentPreview,
  scrollFleetQueue,
} from "./chat-composer-attachments.ts";
import {
  appendMentionOrSlashTrigger,
  COMPOSE_TEXTAREA_SELECTOR,
  readComposeTextareaValue,
  stripTrailingSlashOrMentionToken,
  syncComposeTextarea,
} from "./chat-composer-draft.ts";
import {
  adjustTextareaHeight,
  renderCompactionIndicator,
  renderChatFleetDockFooter,
  renderComposerFooterPill,
  renderComposerReasonChannel,
  renderComposerStatusSlot,
  renderComposeWaitStatus,
  renderFleetComposeControlsDock,
  renderFleetComposeControlsGrowPanel,
  renderFleetSendOptions,
  resolveComposerThinkingSummary,
} from "./chat-composer-shell.ts";
import {
  FLEET_HEARTBEAT_TTL_MS,
  operatorTrustActivityFields,
  renderFleetPlanActivityBanner,
  renderOperatorTrustBanner,
  renderPinnedActiveJobBanner,
} from "./chat-fleet-banners.ts";
import { renderChatStatusStrip, type ChatStatusStripItem } from "./chat-status-strip.ts";
import {
  escapeHtml,
  findLastAssistantGroupKey,
  renderFilesChangedTabRow,
  renderFleetCompleteNotice,
  renderFleetResultNotice,
  renderMinimalStatusRow,
  renderOperatorMilestoneNotice,
  renderPlanApprovalNotice,
  renderSubagentResultsSection,
} from "./chat-thread-notices.ts";
import type { ChatProps } from "./chat-view-props.ts";
import { renderWaitSponsorSlot } from "./chat-wait-sponsor-view.ts";
import { shouldShowWaitSponsorSlot } from "./chat-wait-sponsor.ts";
import { normalizeMessage, normalizeRoleForGrouping } from "./message-normalizer.ts";

/**
 * Render the outgoing transcript snapshot that lives behind the
 * hydration crossfade. The outgoing wrapper is `aria-hidden` and only
 * there so the previous chat's bubbles stay readable while the new
 * payload is in flight — it intentionally skips the subagent /
 * orchestration / strip layers the active thread renders. Pure
 * bubble-only rendering keeps the fade visually identical to the
 * incoming thread's tail without dragging expensive computations.
 */
function buildOutgoingSnapshotGroups(messages: readonly unknown[]): MessageGroup[] {
  const groups: MessageGroup[] = [];
  let current: MessageGroup | null = null;
  for (let i = 0; i < messages.length; i += 1) {
    const message = messages[i];
    if (!message || typeof message !== "object") {
      continue;
    }
    const normalized = normalizeMessage(message);
    const role = normalizeRoleForGrouping(normalized.role);
    const id = normalized.id ?? `outgoing-${i}`;
    if (!current || current.role !== role) {
      if (current) {
        groups.push(current);
      }
      current = {
        kind: "group",
        key: `outgoing-group:${role}:${id}`,
        role,
        messages: [{ message, key: id }],
        timestamp: normalized.timestamp || 0,
        isStreaming: false,
      };
      continue;
    }
    current.messages.push({ message, key: id });
    if ((normalized.timestamp || 0) > current.timestamp) {
      current.timestamp = normalized.timestamp || 0;
    }
  }
  if (current) {
    groups.push(current);
  }
  return groups;
}

/**
 * Pure read-only rendering of the previous transcript used as the
 * outgoing layer of the hydration crossfade. No event handlers, no
 * scroll capture, no search input — the wrapper sits behind an
 * absolutely-positioned dimmed shell.
 */
function renderOutgoingSnapshot(params: {
  messages: readonly unknown[];
  assistantName?: string;
  assistantAvatar?: string | null;
  transcriptMode?: boolean;
}) {
  const groups = buildOutgoingSnapshotGroups(params.messages);
  const assistantName = params.assistantName ?? "Assistant";
  return html`
    <div class="chat-thread ${params.transcriptMode ? "chat-transcript" : ""}" role="presentation">
      ${groups.map((group) =>
        renderMessageGroup(group, {
          showReasoning: false,
          transcriptMode: params.transcriptMode ?? false,
          assistantName,
          assistantAvatar: params.assistantAvatar ?? null,
        }),
      )}
    </div>
  `;
}

export function renderChat(props: ChatProps) {
  const canCompose = props.connected;
  // JAISIU-3337: fold the three "is this session alive?" producers
  // (chat.send, fleet-question pending, active subagents) into one
  // `liveness` value. `isLive` replaces the previous three-boolean
  // OR — a row is now live when ANY producer says so, which closes
  // the drift where a pending fleet-question made the row look idle.
  // The composition is cheap (pure filter + OR); see
  // `session-liveness.ts` for the rationale and tests.
  //
  // JAISIU-3333 (UX-R9): `liveness.isLive` is the *consumer* side of
  // the message-completion state machine in
  // `../chat/message-completion-state.ts`. The state machine is the
  // explicit source of truth (`streaming | awaiting_user |
  // completed`); this boolean is its three-way OR'd projection. The
  // invariant enforced there — "chat-final is the SOLE event that
  // exits `streaming`; assistant-delta never closes the turn" — is
  // the same invariant the chat-controller upholds by only clearing
  // `state.chatStream` / `state.chatRunId` inside `handleChatEvent`'s
  // `payload.state === "final"` branch. If this OR ever starts to
  // include a fourth signal, the projection below would silently
  // diverge from the state machine — keep them in lockstep.
  const liveness = computeSessionLiveness({
    runActive: Boolean(props.canAbort) || props.sending || props.stream !== null,
    backgroundRunsActive: (props.sessionBackgroundRunIds?.length ?? 0) > 0,
    fleetQuestion:
      props.fleetQuestion && props.fleetQuestion.sessionKey === props.sessionKey
        ? props.fleetQuestion
        : null,
    subagents: sessionChildren(props),
    lastEventAtMs: props.lastChatEventAt ?? null,
    nowMs: props.chatWaitNow ?? Date.now(),
    heartbeatTtlMs: FLEET_HEARTBEAT_TTL_MS,
  });
  const runActive = liveness.isLive;
  // JAISIU-3333 (UX-R9): the three-way `streaming / awaiting_user /
  // completed` state machine in
  // `../chat/message-completion-state.ts` is the formal source of
  // truth for the chat-composer gate. The boolean projected above
  // (`runActive = liveness.isLive`) is the same gate: every
  // `isMessageCompletionBlocking("streaming"|"awaiting_user")` cell
  // in the transition table reduces to `true`, every
  // `isMessageCompletionBlocking("completed")` cell reduces to
  // `false`, and the controller's `handleChatEvent` upholds I2
  // (assistant-delta never exits `streaming`) by only clearing
  // `state.chatStream` / `state.chatRunId` on `chat-final`. If this
  // derivation ever gains a fourth producer, the projection below
  // will silently diverge from the state machine — re-read both
  // modules together.
  const showRunChrome = shouldShowLiveRunIndicator({
    sending: props.sending,
    canAbort: props.canAbort,
    stream: props.stream,
    streamStartedAt: props.streamStartedAt,
    nowMs: props.chatWaitNow ?? Date.now(),
    toolMessageCount: props.toolMessages?.length ?? 0,
    // JAISIU-activity-always-visible — keep the live chrome up while
    // subagents are running even when the parent chat.run is idle.
    subagentLiveCount: liveness.activeRunCount,
  });
  const isBusy = runActive;
  const canAbort = Boolean(props.canAbort && props.onAbort);
  // JAISIU-3297 UX-R8 (operator feedback 2026-09-14): Stop thinking no
  // longer has a dedicated button. It is a Shift-click modifier on the
  // unified Stop button. `canStopThinking` now gates whether the
  // destructive Shift-click affordance is surfaced (the ⇧ hint + the
  // branch that calls `props.onStopThinking`). Same single-source-of-truth
  // contract as before: requires the wired handler so the host can opt
  // out of the destructive path without leaking state into the view.
  const canStopThinking = canAbort && Boolean(props.onStopThinking);
  const hasDraft = props.draft.trim().length > 0;
  const hasAttachments = (props.attachments?.length ?? 0) > 0;
  const attachmentsUploading = Boolean(props.attachments?.some((a) => a.uploading));
  const hasPendingPayload = hasDraft || hasAttachments;
  const canSubmit = props.connected && props.canSend && hasPendingPayload && !attachmentsUploading;
  const activeSession = props.sessions?.sessions?.find((row) => row.key === props.sessionKey);
  // BIBLE §3 + a11y: the chat route needs exactly one <h1>. It is the
  // session title, rendered as the transcript column's heading rather
  // than as a separate banner, so it costs no extra vertical space.
  const chatHeadingText =
    activeSession?.derivedTitle?.trim() ||
    activeSession?.subject?.trim() ||
    activeSession?.label?.trim() ||
    activeSession?.displayName?.trim() ||
    "Chat";

  // BIBLE §3 — blockers, errors and decision requests must be visible
  // WITHOUT opening the agent activity / Inspector panel. Derive them
  // from the state the chat already has.
  const chatStatusItems: ChatStatusStripItem[] = [];
  const pendingQuestion = props.fleetQuestion;
  if (pendingQuestion && !pendingQuestion.answered) {
    chatStatusItems.push({
      id: `question:${pendingQuestion.id}`,
      tone: "blocked",
      label: pendingQuestion.title.trim() || "The agent is waiting for your decision.",
      detail: pendingQuestion.questions.length
        ? `${pendingQuestion.questions.length} question${pendingQuestion.questions.length === 1 ? "" : "s"} to answer`
        : null,
    });
  }
  const failedTodo = (props.fleetTodos ?? []).find(
    (todo) => todo.status === "cancelled" && todo.failureReason?.trim(),
  );
  if (failedTodo) {
    chatStatusItems.push({
      id: `todo:${failedTodo.id}`,
      tone: "error",
      label: failedTodo.content.trim(),
      detail: failedTodo.failureReason?.trim() ?? null,
    });
  }
  const blockedTodo = (props.fleetTodos ?? []).find(
    (todo) => todo.status === "pending" && /blocked|waiting for|needs decision/i.test(todo.content),
  );
  if (blockedTodo) {
    chatStatusItems.push({
      id: `todo-blocked:${blockedTodo.id}`,
      tone: "warning",
      label: blockedTodo.content.trim(),
      detail: blockedTodo.validationNotes?.join(" · ")?.trim() || null,
    });
  }
  if (props.error?.trim() && props.error !== chatStatusItems[0]?.detail) {
    chatStatusItems.push({
      id: "chat-error",
      tone: "error",
      label: props.error.trim(),
      detail: null,
    });
  }
  const contextUsage = resolveContextUsage(activeSession, props.sessions?.defaults, {
    messages: props.messages,
    pending: props.sending,
  });
  const assistDisplay = resolveAssistDisplay(props);
  const showThinking = assistDisplay === "on";
  const transcriptMode = Boolean(props.transcriptMode);
  const reasoningExpanded = Boolean(props.reasoningExpanded);
  const reasoningLevel = activeSession?.reasoningLevel ?? "off";
  const showReasoning =
    showThinking && (reasoningLevel !== "off" || Boolean(props.reasoningStream?.trim()));
  const assistantIdentity = {
    name: props.assistantName,
    avatar: props.assistantAvatar ?? props.assistantAvatarUrl ?? null,
  };

  const chatItems: Array<ChatItem | MessageGroup | AssistantTurn> = buildChatItems(props);
  // DEBUG: surface length to console for the env-panel test.
  // @ts-expect-error - intentional debug
  if (typeof globalThis !== "undefined") globalThis.__chatItems_len = chatItems.length;
  const lastAssistantGroupKey = findLastAssistantGroupKey(chatItems);
  // JAISIU-wait-sponsor — the slot is gated on run state, not on a timer,
  // so it unmounts on the same render tick as the run chrome. `chatRunId`
  // clears via clearChatWaitState on abort and on chat-final, and
  // `turnCommitted` flips once the assistant writes its final bubble.
  const sponsorTurnCommitted = props.messages.some((raw) => {
    if (!raw || typeof raw !== "object") {
      return false;
    }
    const m = raw as { streaming?: unknown; role?: unknown };
    return m.role === "assistant" && m.streaming !== true;
  });
  const waitSponsorVisible = shouldShowWaitSponsorSlot({
    connected: props.connected,
    chatRunId: props.chatRunId ?? null,
    runActive: showRunChrome,
    subagentRunStatuses: sessionChildren(props)
      .map((child) => String((child as { status?: unknown }).status ?? ""))
      .filter((status) => status.length > 0),
    turnCommitted: sponsorTurnCommitted,
  });
  const history = Array.isArray(props.messages) ? props.messages : [];
  const resolveRewindTarget = (message: unknown) => resolveMessageRewindTarget(history, message);

  // JAISIU-3655 (followup) — expose the same `searchQRaw` /
  // `effectiveSearch` pair that `buildChatItems` uses internally so
  // the no-matches bracket below can echo the query back without
  // going through a second regex build. (Previously this ternary
  // referenced locals that only existed inside `buildChatItems` —
  // a regression that the browser tests caught when the empty-state
  // path was rendered.)
  const searchQRaw = props.chatMessageSearchQuery ?? "";
  const effectiveSearch = searchQRaw.trim().length > 0;

  // Build the inline fleet-question card AT MOST ONCE. It is rendered at
  // the bottom of the last assistant message group ("end of answer"),
  // not as a sibling of the message list. This satisfies the operator
  // ask that the selectable questionnaire be attached to the answer,
  // not floating mid-thread.
  const inlineFleetQuestionNode = props.fleetQuestion
    ? renderFleetQuestionInline({
        request: props.fleetQuestion,
        draft: props.fleetQuestionDraft ?? {},
        freeTextByQuestion: props.fleetQuestionFreeText ?? {},
        busy: props.fleetQuestionBusy === true,
        error: props.fleetQuestionError ?? null,
        otherSession: props.fleetQuestion.sessionKey.trim() !== (props.sessionKey ?? "").trim(),
        onToggleOption: (questionId, optionId, allowMultiple) =>
          props.onFleetQuestionToggleOption?.(questionId, optionId, allowMultiple),
        onFreeTextChange: (questionId, value) =>
          props.onFleetQuestionFreeTextChange?.(questionId, value),
        onSubmit: () => {
          void props.onFleetQuestionSubmit?.();
        },
      })
    : nothing;

  // The fleet-question card and the answer-echo bubble render inside
  // `.chat-thread` (so they inherit the centered column layout), as the
  // LAST siblings of the `repeat()` of `chatItems`. Both must be declared
  // BEFORE the `const thread = html\`` template literal so they're not in
  // the temporal dead zone when Lit evaluates the template — JAISIU-3156.
  const fleetQuestionBlock = props.fleetQuestion
    ? html`
        <div class="chat-group chat-group--fleet-question" data-role="fleet-question">
          <div class="chat-group-avatar">
            <span class="chat-group-avatar__initial" aria-hidden="true">?</span>
          </div>
          <div class="chat-group-messages">
            <div class="chat-group-header">
              <span class="chat-sender-name">Jaisiu asks</span>
              <span class="chat-group-timestamp"> ${props.fleetQuestion.title} </span>
            </div>
            ${inlineFleetQuestionNode}
          </div>
        </div>
      `
    : nothing;

  // Collapse immediately into a compact bubble. Hide it once the
  // assistant run/stream resumes so the live reply is the last message
  // (otherwise the echo stays glued under the stream and the thread
  // never feels like it progressed).
  const suppressAnswerEcho =
    props.stream != null || props.sending === true || Boolean(props.chatRunId);
  const fleetAnswerEchoBlock =
    props.fleetQuestionLastAnswer && !suppressAnswerEcho
      ? html`
          <div
            class="chat-group chat-group--user chat-group--fleet-answer"
            data-role="fleet-question-answer"
            data-collapsed="true"
          >
            <div class="chat-group-avatar chat-group-avatar--user">
              <span class="chat-group-avatar__initial" aria-hidden="true">Y</span>
            </div>
            <div class="chat-group-messages">
              <div class="chat-group-header">
                <span class="chat-sender-name">You answered</span>
                <span class="chat-group-timestamp">
                  ${props.fleetQuestionLastAnswer.requestTitle}
                </span>
              </div>
              <div class="chat-message chat-message--text">
                <div class="chat-message-text">
                  ${props.fleetQuestionLastAnswer.answers.map(
                    (a) => html`
                      <div class="chat-fleet-answer-echo chat-fleet-answer-echo--collapsed">
                        <div class="chat-fleet-answer-echo__prompt">${a.questionPrompt}</div>
                        <div class="chat-fleet-answer-echo__value">
                          ${formatFleetAnswerEchoValue(a)}
                        </div>
                      </div>
                    `,
                  )}
                </div>
              </div>
            </div>
          </div>
        `
      : nothing;

  // Wrap the parent's onSend so we reset the composer height synchronously the
  // moment the user submits (Enter or Send button). Without this, the resize
  // waited for Lit's next re-render microtask and could appear to lag behind
  // the message being dispatched. With adjustTextareaHeight clearing the
  // inline minHeight, the post-send re-render also produces the correct
  // natural height even when the textarea remains focused.
  const handleComposerSend = () => {
    const liveDraft = readComposeTextareaValue() || props.draft;
    if (liveDraft !== props.draft) {
      props.onDraftChange(liveDraft);
    }
    const localDraft = applyComposerLocalSlashFromDraft(liveDraft);
    if (localDraft != null) {
      props.onDraftChange(localDraft);
      return;
    }
    const el = document.querySelector<HTMLTextAreaElement>(COMPOSE_TEXTAREA_SELECTOR);
    if (el) {
      adjustTextareaHeight(el);
    }
    props.onSend();
  };
  const editingRewindTarget: MessageRewindTarget | null =
    props.chatEditingPriorMessage && props.chatPendingRewindTarget
      ? props.chatPendingRewindTarget
      : null;
  const messageActions =
    props.onEditUserMessage || props.onRegenerateAssistant
      ? {
          onEditUser: props.onEditUserMessage,
          onRegenerate: props.onRegenerateAssistant,
        }
      : undefined;
  const errorDisplay = parseDisplayChatError(props.error);
  // UX-PLAN Pillar D — prefer the snapshot's children so the thread
  // and the inspector never disagree. Falls back to `chatSubagents`
  // for callers that haven't wired the snapshot yet.
  const subagentCards = sessionChildren(props).filter(isSubagentRunCard);
  const hasActiveSubagent = subagentCards.some(
    (c) => resolveSubagentActivityPhase(c) === "ongoing",
  );

  const heartbeatActive = resolveChatHeartbeatActive({
    messages: props.messages,
    runActive: isBusy,
  });

  const composePlaceholder = props.connected
    ? hasAttachments
      ? "Add a message or attach more files…"
      : "Message this session"
    : "Connect to the gateway to start chatting…";

  const mentionAgents = (props.agentsList?.length ? props.agentsList : null) ?? props.agents ?? [];
  const fleetRoutingPreview =
    props.onChatSendOrchestrateAllChange && hasDraft
      ? previewFleetSendRouting({
          draft: props.draft,
          sessionKey: props.sessionKey,
          connected: props.connected,
          composerMode: props.composerMode ?? "agent",
          orchestrateWithAll: props.chatSendOrchestrateAll ?? false,
          createNewProject: props.chatSendCreateNewProject ?? false,
          selfImprovement: props.chatSendSelfImprovement ?? false,
          sendProjectId: props.chatSendProjectId ?? null,
          projects: projectsForChatContext(props.fleetProjects ?? [], props.sessionKey),
          agents: mentionAgents.map((a) => ({ id: a.id, name: a.name })),
        })
      : null;
  const slashCommands = listComposerSlashCommands();
  const slash = extractSlashCommandQuery(props.draft);
  const slashMatches = slash.open ? filterSlashCommands(slashCommands, slash.query) : [];
  const slashOpen = slash.open && slashMatches.length > 0;
  const slashIndex = Math.max(
    0,
    Math.min(props.slashCommandIndex ?? 0, Math.max(0, slashMatches.length - 1)),
  );
  const mention = extractMentionQuery(props.draft);
  const mentionMatches =
    slashOpen || !mention.open ? [] : filterAgents(mentionAgents, mention.query);
  const mentionOpen = mention.open && mentionMatches.length > 0;
  const fileContext = extractFileContextQuery(props.draft);
  const fileContextMatches =
    fileContext.open && (props.chatWorkspaceTree?.length ?? 0) > 0
      ? filterWorkspacePaths(props.chatWorkspaceTree ?? [], fileContext.query)
      : [];
  const fileContextOpen = fileContext.open && fileContextMatches.length > 0;
  const fileContextIndex = Math.max(
    0,
    Math.min(props.chatFileContextIndex ?? 0, Math.max(0, fileContextMatches.length - 1)),
  );

  // Folder context handling (@folder: or @dir:)
  const folderContext = extractFolderContextQuery(props.draft);
  const folderContextMatches =
    folderContext.open && (props.chatWorkspaceTree?.length ?? 0) > 0
      ? filterFolderPaths(props.chatWorkspaceTree ?? [], folderContext.query)
      : [];
  const folderContextOpen = folderContext.open && folderContextMatches.length > 0;
  const folderContextIndex = Math.max(
    0,
    Math.min(props.chatFolderContextIndex ?? 0, Math.max(0, folderContextMatches.length - 1)),
  );

  const mentionIndex = Math.max(
    0,
    Math.min(props.mentionIndex ?? 0, Math.max(0, mentionMatches.length - 1)),
  );
  const commitSlashCommand = (command: (typeof slashCommands)[number]) => {
    const localDraft = applyComposerLocalSlashCommand(command, props.draft);
    if (localDraft != null) {
      props.onDraftChange(localDraft);
      return;
    }
    props.onSlashCommandSelect?.(formatSlashCommandDraft(command));
  };
  const commitMention = (agentId: string) => {
    props.onMentionSelect?.(agentId);
  };
  const commitFileContext = (path: string) => {
    props.onFileContextSelect?.(path);
  };
  const commitFolderContext = (path: string) => {
    props.onFolderContextSelect?.(path);
  };
  const activityAgentId = resolveAgentIdForSession(props.sessionKey);

  const sidebarOpen = Boolean(props.sidebarOpen && props.onCloseSidebar);
  const thread = html`
    <div
      class="chat-thread ${transcriptMode ? "chat-transcript" : ""}"
      role="log"
      aria-live="polite"
    >
      ${repeat(
        chatItems,
        (item: ChatItem | MessageGroup | AssistantTurn) => item.key,
        (item: ChatItem | MessageGroup | AssistantTurn) => {
          if (item.kind === "older-loading-indicator") {
            // JAISIU-3473 — sticky indicator at the top of the
            // thread. CSS `position: sticky; top: 0` keeps it pinned
            // without inserting whitespace above the messages.
            return html`
              <div
                class="chat-older-loading-indicator"
                role="status"
                aria-live="polite"
                aria-label="Loading older messages"
                data-testid="chat-older-loading-indicator"
              >
                <span class="chat-older-loading-indicator__spinner" aria-hidden="true"></span>
                <span class="chat-older-loading-indicator__label">Loading older messages…</span>
              </div>
            `;
          }

          if (item.kind === "reading-indicator") {
            return renderReadingIndicatorGroup(assistantIdentity);
          }

          if (item.kind === "minimal-status") {
            return renderMinimalStatusRow(item.label, item.startedAt, props.chatWaitNow);
          }

          if (item.kind === "file-changes") {
            return renderFilesChangedTabRow(item, { onFileClick: props.onFileChangesFileClick });
          }

          if (item.kind === "fleet-complete") {
            return renderFleetCompleteNotice(item);
          }

          if (item.kind === "fleet-result") {
            return renderFleetResultNotice(item);
          }

          if (item.kind === "plan-approval") {
            return renderPlanApprovalNotice(item);
          }

          if (item.kind === "subagent-results") {
            return renderSubagentResultsSection(item, {
              isExpanded: Boolean(props.chatSubagentExpanded?.[item.key]),
              onToggle: () => props.onToggleSubagentExpand?.(item.key),
            });
          }

          if (item.kind === "operator-milestone") {
            return renderOperatorMilestoneNotice(item);
          }

          if (item.kind === "assistant-turn") {
            // Single unified assistant turn — collapsible activity +
            // final answer. The renderer delegates to `renderChatLiveRunGroup`
            // internally while the run is still active, so the live badge
            // ("Working · N steps") keeps its existing animation.
            return renderAssistantTurnGroup(item, {
              assistant: assistantIdentity,
              showReasoning,
              reasoningExpanded,
              transcriptMode,
              messageActions,
              rewindTarget: null,
              editingRewindTarget: null,
              isLastAssistantGroup: item.key === lastAssistantGroupKey,
              onFileClick: props.onFileChangesFileClick,
              onOpenSidebar: props.onOpenSidebar,
              onOpenViewer: props.onOpenMessageViewer,
              chatWaitNow: props.chatWaitNow,
              inlineFleetQuestion:
                item.key === lastAssistantGroupKey ? inlineFleetQuestionNode : undefined,
              subagentsForLive: sessionChildren(props),
              compactionStatus: props.compactionStatus ?? null,
              liveRunHint: props.liveRunHint ?? null,
              activeFileToolPath: props.chatActiveFileToolPath ?? null,
              hasActiveTool: hasActiveToolInStream(props.toolMessages ?? []),
              // JAISIU-3551 — avatar-driven manual collapse state (mirrors
              // the user-bubble path so assistant turns are collapsible too).
              collapsedMessageIds: props.collapsedMessageIds,
              onToggleMessageCollapsed: props.onToggleMessageCollapsed,
            });
          }

          if (item.kind === "stream") {
            return renderStreamingGroup(
              item.text,
              item.startedAt,
              props.onOpenSidebar,
              assistantIdentity,
              props.onOpenMessageViewer,
            );
          }

          if (item.kind === "subagent-run") {
            // Build a childSessionKey→card map for the in-thread chat list
            // so each row can resolve its parent in O(1) without re-walking
            // the same list. The list also feeds the parent badge + the
            // "is this row a child of the currently-selected parent?" check
            // (parent → children highlight).
            const allChildren = sessionChildren(props);
            const card = allChildren.find((c) => c.id === item.cardId);
            if (!card) {
              return nothing;
            }
            const isFinished =
              card.status === "done" || card.status === "error" || card.status === "cancelled";
            const pending = props.chatSubagentPendingActions?.[card.id];
            // Parent → children: resolve the in-scope parent (if any) by
            // looking up card.parentSessionKey against the childSessionKey
            // map. We also fold the panel selection into the in-thread row
            // so the operator sees the same highlight in the chat. Logic
            // lives in `resolveSubagentParentContext` so the in-thread
            // card and the inspector Subagents tab agree.
            const parentCtx = resolveSubagentParentContext(
              card,
              allChildren,
              props.selectedSubagentParentId,
            );
            return renderSubagentRunRow({
              card,
              // JAISIU-XXXX — every subagent run row lands **collapsed** by
              // default. The operator asked for the transcript to stay
              // smooth; a freshly-spawned subagent bubble now appears as a
              // one-line status row (chevron + status + title + age). The
              // Open session / Cancel / Retry / stream body only opens when
              // the operator clicks the header.
              //
              // Finished runs use `terminalOpen` (instead of `expanded`)
              // for the same reason — long lists of settled runs must not
              // shadow the chat. The operator can still click to peek.
              expanded: isFinished ? undefined : props.chatSubagentExpanded?.[card.id],
              terminalOpen: isFinished
                ? Boolean(props.chatSubagentTerminalOpen?.[card.id])
                : undefined,
              defaultExpanded: false,
              onToggle: () =>
                isFinished
                  ? props.onToggleTerminalSubagentOpen?.(card.id)
                  : props.onToggleSubagentExpand?.(card.id),
              onOpenSessionTab: card.childSessionKey
                ? () =>
                    props.onOpenSubagentSession?.({
                      childKey: card.childSessionKey,
                      parentKey: props.sessionKey,
                      label: card.label,
                      task: card.task,
                      runId: card.runId,
                    })
                : undefined,
              onCancel:
                card.childSessionKey && props.onCancelSubagentRun
                  ? () => props.onCancelSubagentRun?.(card)
                  : undefined,
              onRetry: props.onRetrySubagentRun
                ? () => props.onRetrySubagentRun?.(card)
                : undefined,
              cancelling: pending === "cancel",
              retrying: pending === "retry",
              // Parent → children hierarchy wiring (AC: parent session
              // identifiable on every child card, selection highlights
              // children both in the inspector and the chat list).
              parentLabel: parentCtx.parentLabel,
              parentShortId: parentCtx.parentShortId,
              isHighlightedChild: parentCtx.isHighlightedChild,
            });
          }

          if (item.kind === "orchestration-group") {
            return renderOrchestrationGroup(item, props);
          }

          if (item.kind === "group") {
            return renderMessageGroup(item, {
              onOpenSidebar: props.onOpenSidebar,
              onOpenViewer: props.onOpenMessageViewer,
              showReasoning,
              reasoningExpanded,
              transcriptMode,
              assistantName: props.assistantName,
              assistantAvatar: assistantIdentity.avatar,
              messageActions,
              resolveRewindTarget,
              editingRewindTarget,
              isLastAssistantGroup: item.key === lastAssistantGroupKey,
              onFileClick: props.onFileChangesFileClick,
              // JAISIU-3551 — avatar-driven manual collapse state
              collapsedMessageIds: props.collapsedMessageIds,
              onToggleMessageCollapsed: props.onToggleMessageCollapsed,
              // JAISIU-XXXX — brief highlight on the scroll-restored anchor
              anchorHighlightMessageId: props.scrollHighlightMessageId ?? null,
              resolveSessionFileUrl: props.resolveSessionFileUrl,
              resolveSessionFilePreviewUrl: props.resolveSessionFilePreviewUrl,
              // JAISIU-4449 — load the prepared preview (tabular schema
              // / extracted text) when the overlay opens for a
              // spreadsheet or a document.
              loadSessionFilePreview: props.loadSessionFilePreview,
            });
          }

          return nothing;
        },
      )}
      ${fleetQuestionBlock} ${fleetAnswerEchoBlock}
    </div>
  `;

  // Session navigator (open tabs, path rows, Reset Node) lives in
  // Inspector → Agents → Sessions, not above the transcript. The slim
  // "Open Sessions" flyout in the left chat sidebar remains as a
  // quick-access shortcut.
  const showSessionTabs = true;
  const showComposerToolbar = Boolean(props.onSelectChatTarget && props.onSelectChatModel);
  const tabLabelHints = buildChatTabLabelHints(sessionChildren(props), props.sessions);
  const sessionProjects = projectsForChatContext(props.fleetProjects ?? [], props.sessionKey);
  const multiAgentHintBanner =
    !props.multiAgentHintDismissed && detectMultiAgentIntent(props.draft)
      ? renderMultiAgentHint({
          draft: props.draft,
          dismissed: false,
          onApplyAll: (next) => props.onDraftChangeForMultiAgent?.(next),
          onDismiss: () => props.onMultiAgentHintDismiss?.(),
        })
      : nothing;

  const fleetStripsClass = transcriptMode ? "chat-fleet-strips--transcript" : "";
  // Focus-mode entry was removed in JAISIU-3044. The legacy exit button is
  // no longer reachable (no user toggle exists any more); the onboarding
  // shell uses the same `shell--chat-focus` class but with its own UI.
  const fleetV2FocusExit = nothing;

  const fleetV2DistractionExit =
    assistDisplay === "minimal"
      ? html`
          <div class="chat-distraction-banner" role="status">
            <span class="chat-distraction-banner__text"
              >Distraction-free mode — showing only your messages and completion notices.</span
            >
            <button
              type="button"
              class="btn btn--sm chat-distraction-banner__exit"
              @click=${() => props.onSetAssistDisplay("on")}
            >
              Show full output
            </button>
          </div>
        `
      : nothing;

  const hasOutgoingTranscript = (props.hydrationOutgoing?.messages?.length ?? 0) > 0;
  const hydrationMode = props.hydrationMode ?? "idle";
  const showTranscriptOverlay = shouldShowChatTranscriptOverlay({
    transcriptLoading: props.transcriptLoading === true,
    historyLoading: props.loading,
    hasTranscriptItems: chatItems.length > 0,
    hasOutgoingTranscript,
    hydrationMode,
  });
  const showSkeleton = shouldShowTranscriptSkeleton({
    historyLoading: props.loading,
    hasTranscriptItems: chatItems.length > 0,
    hydrationMode,
  });
  const showHairline = shouldShowTranscriptProgressHairline({
    historyLoading: props.loading,
    hydrationMode,
  });
  const incomingHydrationClass =
    hydrationMode === "cold"
      ? "chat-thread-hydration chat-thread-hydration--incoming"
      : hydrationMode === "warm"
        ? "chat-thread-hydration chat-thread-hydration--warm"
        : "";

  const fleetV2ThreadPane = html`
    ${fleetV2FocusExit} ${fleetV2DistractionExit}

    <div
      class="chat-split-container ${sidebarOpen ? "chat-split-container--open" : ""}"
      style=${
        sidebarOpen
          ? `--chat-split-ratio: ${String(Math.round((props.splitRatio ?? 0.6) * 100))}%`
          : ""
      }
    >
      <div class="chat-main chat-main--thread chat-main--hydration-${hydrationMode}">
        <h1 class="chat-thread__heading" data-testid="chat-thread-heading">${chatHeadingText}</h1>
        ${
          showHairline
            ? renderChatTranscriptProgressHairline({
                error: props.historyHydrationError,
                onRetry: props.onRetryHistoryHydration,
              })
            : nothing
        }
        ${showTranscriptOverlay ? renderChatTranscriptLoadingOverlay() : nothing}
        ${
          hasOutgoingTranscript
            ? html`
                <div
                  class="chat-thread-hydration chat-thread-hydration--outgoing"
                  aria-hidden="true"
                >
                  <div class="chat-thread-scroll chat-fleet-thread-scroll">
                    ${renderOutgoingSnapshot({
                      messages: props.hydrationOutgoing?.messages ?? [],
                      assistantName: props.assistantName,
                      assistantAvatar: assistantIdentity.avatar,
                      transcriptMode,
                    })}
                  </div>
                </div>
              `
            : nothing
        }
        <div
          class="chat-thread-scroll chat-fleet-thread-scroll ${incomingHydrationClass}"
          ?inert=${showTranscriptOverlay}
          @scroll=${props.onChatScroll}
        >
          ${showSkeleton ? renderChatTranscriptSkeleton() : nothing}
          ${
            props.onChatMessageSearchQueryChange
              ? html`
                  <div class="chat-thread-search">
                    <input
                      type="search"
                      class="chat-thread-search__input"
                      placeholder="Search in this thread…"
                      aria-label="Search in this thread"
                      data-testid="chat-thread-search-input"
                      .value=${props.chatMessageSearchQuery ?? ""}
                      @input=${(e: Event) =>
                        props.onChatMessageSearchQueryChange?.(
                          (e.target as HTMLInputElement).value,
                        )}
                      @keydown=${(e: KeyboardEvent) => {
                        if (e.key === "Enter" && props.onChatMessageSearchNav) {
                          e.preventDefault();
                          props.onChatMessageSearchNav(e.shiftKey ? -1 : 1);
                          return;
                        }
                        // JAISIU-3657 — pressing Escape clears the
                        // in-thread search AND restores the
                        // pre-search reading position. We funnel
                        // through the same input-clear path the
                        // native (×) button uses so the host's
                        // `handleChatMessageSearchClear` runs once
                        // and the anchors are reset exactly once.
                        // `preventDefault` first so the browser does
                        // not also clear the value on Esc for
                        // `type="search"` (which would otherwise
                        // fire an `input` event after the keydown
                        // and double-run the reset).
                        if (e.key === "Escape") {
                          const hasQuery = (props.chatMessageSearchQuery ?? "").trim().length > 0;
                          if (!hasQuery) return;
                          if (!props.onChatMessageSearchQueryChange) return;
                          e.preventDefault();
                          props.onChatMessageSearchQueryChange("");
                          return;
                        }
                      }}
                    />
                    ${
                      // JAISIU-3655 — compact counter + ↑/↓ nav. Only
                      // rendered when there is an active query AND at
                      // least one match. The total is computed by the
                      // host from the rendered DOM after each update;
                      // we trust the prop and clamp `currentIndex`
                      // locally for display. The "x" settings
                      // popover trigger lives at the END of this nav
                      // row (after the ↓ next arrow) — operator asked
                      // to move it out from inside the input box
                      // where it was overlapping the counter
                      // (JAISIU-3655 followup).
                      props.chatMessageSearchQuery?.trim() &&
                      (props.chatMessageSearchTotal ?? 0) > 0
                        ? html`
                            <div
                              class="chat-thread-search__nav"
                              data-testid="chat-thread-search-nav"
                              role="group"
                              aria-label="Search match navigation"
                            >
                              <span
                                class="chat-thread-search__counter"
                                data-testid="chat-thread-search-counter"
                                aria-live="polite"
                              >
                                ${Math.min(
                                  (props.chatMessageSearchCurrentIndex ?? 0) + 1,
                                  props.chatMessageSearchTotal ?? 0,
                                )}
                                / ${props.chatMessageSearchTotal ?? 0}
                              </span>
                              <button
                                type="button"
                                class="chat-thread-search__btn"
                                data-testid="chat-thread-search-prev"
                                aria-label="Previous match"
                                title="Previous match (Shift+Enter)"
                                ?disabled=${(props.chatMessageSearchTotal ?? 0) <= 1}
                                @click=${(e: Event) => {
                                  e.preventDefault();
                                  props.onChatMessageSearchNav?.(-1);
                                }}
                              >
                                ↑
                              </button>
                              <button
                                type="button"
                                class="chat-thread-search__btn"
                                data-testid="chat-thread-search-next"
                                aria-label="Next match"
                                title="Next match (Enter)"
                                ?disabled=${(props.chatMessageSearchTotal ?? 0) <= 1}
                                @click=${(e: Event) => {
                                  e.preventDefault();
                                  props.onChatMessageSearchNav?.(1);
                                }}
                              >
                                ↓
                              </button>
                              <fleet-popover
                                class="chat-thread-search__opts"
                                data-testid="chat-thread-search-opts"
                              >
                                <label
                                  class="chat-thread-search__opt"
                                  data-testid="chat-thread-search-opt-activities"
                                >
                                  <fleet-toggle
                                    label="Search in activities"
                                    ?checked=${props.chatMessageSearchInActivities === true}
                                    @change=${(e: CustomEvent<{ checked: boolean }>) => {
                                      props.onChatMessageSearchInActivitiesChange?.(
                                        e.detail.checked,
                                      );
                                    }}
                                  ></fleet-toggle>
                                </label>
                              </fleet-popover>
                            </div>
                          `
                        : nothing
                    }
                  </div>
                `
              : nothing
          }
          ${renderChatPinnedQuestion(props.pinnedUserQuestion)}
          ${
            // JAISIU-3503 / processing-transparency — non-blocking
            // network blip toast. Anchored at the top of the thread
            // so it stays visible while the operator scrolls. The
            // host's `chatWaitNow` ticks drive the countdown.
            props.chatNetworkBlip
              ? renderNetworkBlipToast({
                  blip: props.chatNetworkBlip,
                  now: props.networkBlipNow ?? Date.now(),
                  onRetryNow: () => props.onNetworkBlipRetryNow?.(),
                  onDismiss: () => props.onNetworkBlipDismiss?.(),
                })
              : nothing
          }
          ${
            // JAISIU-3503 / processing-transparency — always-visible
            // run control bar. Shows the prominent Stop button when a
            // run is in flight, and the Retry button when the last
            // run failed and an auto-retry is queued. Sits right
            // above the thread so the operator doesn't have to hunt
            // for the composer's morph-button.
            renderRunControlBar({
              canAbort: canAbort,
              canStopThinking,
              lastRunFailed: props.lastRunFailed === true,
              retrySecondsUntilReady: props.retrySecondsUntilReady ?? null,
              onAbort: () => props.onAbort?.(),
              onStopThinking: props.onStopThinking ?? null,
              onRetry: props.onRetry ?? null,
            })
          }
          ${
            props.subagentCardHost
              ? renderSubagentSessionWorkBanner({
                  sessionKey: props.sessionKey,
                  sessions: props.sessions,
                  tabMeta: props.chatTabMeta,
                  cardHost: props.subagentCardHost,
                  liveStream: props.stream,
                  chatSending: props.sending,
                  chatRunId: props.chatRunId ?? null,
                  onReturnToParent: props.onReturnToParentSession,
                })
              : nothing
          }
          ${
            props.chatMainRun && runActive
              ? renderSubagentRunRow({
                  card: props.chatMainRun,
                  // JAISIU-XXXX — main run (the active turn-in-flight) also
                  // lands **collapsed** by default. The operator asked for
                  // the transcript to stay smooth; the live bubble is now
                  // a one-line status row until the operator clicks the
                  // header to peek at the stream. `resolveRowExpanded`
                  // still honors the explicit `chatMainRunExpanded` toggle
                  // (and the operator can re-open anytime).
                  expanded: props.chatMainRunExpanded,
                  defaultExpanded: false,
                  onToggle: () => props.onToggleMainRunExpand?.(),
                })
              : nothing
          }
          ${thread}
          ${
            // JAISIU-3655 (followup) — when the in-thread search
            // filter excludes every message we previously fell
            // through to the env-panel ("user PC tables"), which
            // looked like a bug. Now we show a short bracket with
            // the query echoed back. The env-panel fallback stays
            // for the genuine "fresh chat, no messages yet" case.
            !props.loading && chatItems.length === 0
              ? effectiveSearch
                ? html`
                    <div class="chat-no-matches" data-testid="chat-no-matches">
                      No Matches to &ldquo;<span class="chat-no-matches__query"
                        >${escapeHtml(searchQRaw)}</span
                      >&rdquo;
                    </div>
                  `
                : html`
                    <div class="chat-empty" data-testid="chat-env-panel">
                      ${renderChatEnvironmentPanel(
                        props.environment ?? {
                          loading: false,
                          error: null,
                          data: null,
                          onRefresh: () => undefined,
                        },
                      )}
                    </div>
                  `
              : nothing
          }
        </div>
      </div>
      ${
        // JAISIU-3501: minimal floating chevrons (↑ older / ↓ newer).
        // Anchored to `.chat-main--thread` (the viewport), NOT inside
        // the scroll container — they always sit at the same screen
        // position regardless of scroll position and never push,
        // resize, or reflow messages. The `chat-scroll-nav--hidden`
        // modifier toggles the CSS fade-in/out; the buttons stay in
        // the layout (no layout shift on show/hide).
        html`
          ${
            // JAISIU-3503: a SINGLE chevron sits at the bottom-right of
            // the message column. Its icon + click target depend on the
            // operator's scroll position:
            //   - Scrolled up (away from top) → ↑ icon, jumps to the
            //     top of the thread. This is the "scroll up to load
            //     older messages" affordance. The button is shown
            //     after the operator has scrolled upward by a small
            //     accumulated threshold (controller owns the logic).
            //   - Scrolled away from bottom → ↓ icon, jumps to the
            //     newest message.
            // Only one chevron is ever visible. When the operator is
            // reading near the top, the ↑ is the only action. When
            // they are reading mid-thread (older + newer both
            // possible), the controller picks whichever direction the
            // operator is moving toward — the user's intent shows
            // exactly one icon at a time. No state machine splits
            // between two arrow elements.
            props.chatScrollNavDirection
              ? html`
                  <button
                    type="button"
                    class="chat-scroll-nav chat-scroll-nav--single${props.chatScrollNavDirection === "down" ? " chat-scroll-nav--down" : " chat-scroll-nav--up"}"
                    title=${
                      props.chatScrollNavDirection === "down"
                        ? "Jump to new messages"
                        : "Jump to older messages"
                    }
                    aria-label=${
                      props.chatScrollNavDirection === "down"
                        ? "Jump to new messages"
                        : "Jump to older messages"
                    }
                    @click=${
                      props.chatScrollNavDirection === "down"
                        ? props.onScrollToBottom
                        : props.onScrollToTop
                    }
                  >
                    ${props.chatScrollNavDirection === "down" ? icons.arrowDown : icons.arrowUp}
                  </button>
                `
              : nothing
          }
        `
      }
      ${
        sidebarOpen
          ? html`
              <resizable-divider
                .splitRatio=${props.splitRatio ?? 0.6}
                @resize=${(e: CustomEvent<{ splitRatio: number }>) =>
                  props.onSplitRatioChange?.(e.detail.splitRatio)}
              ></resizable-divider>
              <div class="chat-sidebar">
                ${renderMarkdownSidebar({
                  content: props.sidebarContent ?? null,
                  error: props.sidebarError ?? null,
                  onClose: props.onCloseSidebar!,
                  onViewRawText: () => {
                    if (!props.sidebarContent || !props.onOpenSidebar) {
                      return;
                    }
                    props.onOpenSidebar(`\`\`\`\n${props.sidebarContent}\n\`\`\``);
                  },
                })}
              </div>
            `
          : nothing
      }
    </div>
  `;

  const queuedMessages = props.queue ?? [];
  const fleetV2SendPaneChrome = html`
    ${
      queuedMessages.length
        ? html`
            <div class="chat-queue" id="chat-fleet-queue" role="status" aria-live="polite">
              <div class="chat-queue__title">Queued (${queuedMessages.length})</div>
              <div class="chat-queue__list">
                ${queuedMessages.map(
                  (item) => html`
                    <div class="chat-queue__item">
                      <button
                        type="button"
                        class="chat-queue__text chat-queue__text--editable"
                        title="Edit this queued message in the composer"
                        ?disabled=${!props.onQueueEdit}
                        @click=${() => props.onQueueEdit?.(item.id)}
                      >
                        ${
                          item.text ||
                          (item.attachments?.length ? `Image (${item.attachments.length})` : "")
                        }
                      </button>
                      <button
                        class="btn chat-queue__remove"
                        type="button"
                        aria-label="Remove queued message"
                        @click=${() => props.onQueueRemove(item.id)}
                      >
                        ${icons.x}
                      </button>
                    </div>
                  `,
                )}
              </div>
            </div>
          `
        : nothing
    }
    ${multiAgentHintBanner}
    ${
      props.planImplementShow && props.onImplementPlan && props.onDismissPlanImplement
        ? renderPlanImplementOffer({
            planPreview: props.planImplementPreview,
            onImplement: props.onImplementPlan,
            onDismiss: props.onDismissPlanImplement,
          })
        : nothing
    }
  `;

  const trustActivity = operatorTrustActivityFields(props);
  const lastSignalAgeMs =
    props.operatorTrustLastSignalAt && props.operatorTrustLastSignalAt > 0
      ? Date.now() - props.operatorTrustLastSignalAt
      : null;

  const fleetV2RunContextPane = html`
    ${renderOperatorTrustBanner(props)}
    ${renderFleetRunContextSummary({
      connected: props.connected,
      runActive,
      contextUsage,
      breakdownLoading: props.chatContextBreakdownLoading === true,
      breakdownError: props.chatContextBreakdownError ?? null,
      breakdown: props.chatContextBreakdown ?? null,
      onOpenBreakdown: props.onOpenChatContextPanel,
      onRefreshBreakdown: props.onRefreshChatContextBreakdown,
      turnPhase: trustActivity.turnPhase,
      sessionHealth: trustActivity.sessionHealth,
      sessionHealthReason: trustActivity.sessionHealthReason,
      lastSignalAgeMs,
    })}
    <div class="chat-run-context-pane__body">
      ${
        runActive && props.onChatActivityTabChange
          ? html`
              <div class="chat-activity-dock chat-activity-dock--run-context">
                ${renderChatActivityPanel({
                  tab: props.chatActivityTab ?? "all",
                  onTabChange: (tab) => props.onChatActivityTabChange?.(tab),
                  searchQuery: props.chatActivitySearchQuery,
                  onSearchChange: props.onChatActivitySearchChange,
                  messages: props.messages ?? [],
                  chatToolMessages: props.toolMessages ?? [],
                  stream: props.stream,
                  reasoningStream: props.reasoningStream,
                  subagents: sessionChildren(props),
                  ...trustActivity,
                  showThinking: true,
                  runActive,
                  liveRunHint: props.liveRunHint,
                  activeFileToolPath: props.chatActiveFileToolPath,
                  fleetTodos: props.fleetTodos ?? [],
                  connected: props.connected,
                  agentId: activityAgentId,
                  onEditAgentConfig: props.onEditAgentConfig,
                  onOpenAgentsTab: props.onOpenAgentsTab,
                  onOpenSubagentSession: (childKey, label) =>
                    props.onOpenSubagentSession?.({
                      childKey,
                      parentKey: props.sessionKey,
                      label,
                    }),
                })}
              </div>
            `
          : nothing
      }
      <div class="chat-fleet-strips ${fleetStripsClass}">
        ${renderChatCheckpointsPanel({
          checkpoints: props.chatCheckpoints ?? [],
          loading: props.chatCheckpointsLoading,
          busy: props.chatArtifactReviewBusy,
          collapsed: props.chatCheckpointsCollapsed,
          onToggleCollapsed: props.onToggleChatCheckpointsCollapsed,
          onRefresh: props.onRefreshCheckpoints,
          onRestore: props.onRestoreCheckpoint,
        })}
        ${
          (props.fleetTodos?.length ?? 0) > 0 || runActive
            ? renderFleetTodosStrip({
                todos: props.fleetTodos ?? [],
                collapsed: props.fleetTodosStripCollapsed,
                onToggleCollapsed: props.onToggleFleetTodosStripCollapsed,
                onStatusChange: props.onFleetTodoStatusChange,
                busy: props.fleetTodosBusy,
              })
            : nothing
        }
        ${renderFleetRunActivityStrip({
          runActive: showRunChrome,
          activityLabel: props.liveRunHint,
          activityStickyLabel: props.stickyLiveRunHint ?? null,
          // JAISIU-3752 — the run activity strip is driven by the same
          // buildLiveRunStepsWithSticky resolver, so the per-step
          // thinking-summary step is now wired in here too (alongside
          // the live-hint and subagent steps). The activity strip and
          // the live bubble will render the same model message.
          steps: buildLiveRunStepsWithSticky({
            toolMessageCount: props.toolMessages?.length ?? 0,
            hasActiveTool: hasActiveToolInStream(props.toolMessages ?? []),
            runActive,
            streamText: props.stream ?? "",
            reasoningStreamText: props.reasoningStream ?? "",
            compactionStatus: props.compactionStatus ?? null,
            subagents: sessionChildren(props),
            liveRunHint: props.liveRunHint ?? null,
            stickyLiveRunHint: props.stickyLiveRunHint ?? null,
            activeFileToolPath: props.chatActiveFileToolPath,
            thinkingSummary: resolveComposerThinkingSummary(props),
          }).steps,
          filePreviews: props.runArtifactPreviews,
          onFileClick: props.onRunArtifactFileClick,
          unitDetails: unitDetailsFromFleetTodos(props.fleetTodos ?? []),
        })}
        ${renderWaitSponsorSlot({
          visible: waitSponsorVisible,
          // Rotation index is owned by the shell's sponsor timer; index 0
          // is stable for the first paint of a run so the slot never
          // changes content while the operator is reading it.
          rotationIndex: 0,
          runId: props.chatRunId ?? null,
        })}
      </div>
      ${
        !runActive && (props.chatLastRunFiles?.length ?? 0) > 0 && props.chatLastRunTotals
          ? renderChatComposerFileReview({
              files: props.chatLastRunFiles ?? [],
              totals: props.chatLastRunTotals,
              busy: props.chatArtifactReviewBusy,
              collapsed: props.fleetComposerFileReviewCollapsed,
              onToggleCollapsed: props.onToggleFleetComposerFileReviewCollapsed,
              onFileClick: props.onRunArtifactFileClick,
              onReview: props.onComposerReviewAll,
              onKeepAll: props.onComposerKeepAll,
              onUndoAll: props.onComposerUndoAll,
              error: props.chatComposerFileReviewError,
            })
          : nothing
      }
      ${
        props.chatFileDiffOpen && props.onCloseChatFileDiff
          ? renderChatFileDiffPanel({
              detail: props.chatFileDiffDetail ?? null,
              loading: props.chatFileDiffDetailLoading,
              error: props.chatFileDiffError,
              view: props.chatFileDiffView ?? "diff",
              splitView: props.chatFileDiffSplitView,
              showReviewActions: Boolean(
                props.chatFileDiffOpen?.changeId &&
                props.chatPendingReviewChangeIds?.includes(props.chatFileDiffOpen.changeId),
              ),
              reviewBusy: props.chatArtifactReviewBusy,
              onViewChange: (view) => props.onChatFileDiffViewChange?.(view),
              onSplitViewChange: (next) => props.onChatFileDiffSplitViewChange?.(next),
              onClose: props.onCloseChatFileDiff,
              onOpenInProjectFiles: props.onOpenArtifactInProjectFiles,
              onAccept:
                props.chatFileDiffOpen?.changeId && props.onAcceptArtifactChange
                  ? () => props.onAcceptArtifactChange?.(props.chatFileDiffOpen!.changeId)
                  : undefined,
              onReject:
                props.chatFileDiffOpen?.changeId && props.onRejectArtifactChange
                  ? () => void props.onRejectArtifactChange?.(props.chatFileDiffOpen!.changeId)
                  : undefined,
            })
          : nothing
      }
    </div>
  `;

  const fleetComposeScrollChrome = html`
    <div class="chat-compose-layer chat-compose-layer--chrome">
      <div
        class="chat-compose chat-compose--fleet ${props.fleetChatComposerCollapsed ? "chat-compose--fleet-collapsed" : ""}"
      >
        ${
          props.onToggleFleetChatComposerCollapsed
            ? html`
                <div class="chat-compose__chrome-row">
                  <button
                    type="button"
                    class="chat-compose__focus-btn btn subtle btn--sm"
                    title=${
                      props.fleetChatComposerCollapsed
                        ? FLEET_CHAT_FOCUS_HINTS.composerToolbarExpand
                        : FLEET_CHAT_FOCUS_HINTS.composerToolbarCollapse
                    }
                    aria-label=${props.fleetChatComposerCollapsed ? "Show composer toolbar" : "Collapse composer toolbar"}
                    aria-expanded=${!props.fleetChatComposerCollapsed}
                    @click=${props.onToggleFleetChatComposerCollapsed}
                  >
                    ${props.fleetChatComposerCollapsed ? "▸ Toolbar" : "▾ Focus messages"}
                  </button>
                </div>
              `
            : nothing
        }
        ${
          showComposerToolbar && !props.fleetChatComposerCollapsed
            ? renderChatComposerToolbar({
                sessionKey: props.sessionKey,
                connected: props.connected,
                hello: props.hello ?? null,
                sessions: props.sessions,
                employees: props.employees ?? [],
                agents: props.agentsList ?? [],
                modelCatalog: props.modelCatalog ?? [],
                modelCatalogLoading: props.modelCatalogLoading ?? false,
                modelCatalogError: props.modelCatalogError ?? null,
                modelPatching: props.modelPatching ?? false,
                chatSending: props.sending,
                chatMessages: props.messages,
                composerMode: props.composerMode ?? "agent",
                onComposerModeChange: props.onComposerModeChange!,
                onSelectTarget: props.onSelectChatTarget!,
                onSelectModel: props.onSelectChatModel!,
              })
            : nothing
        }
        ${renderComposeWaitStatus(props, isBusy)}
        ${renderFleetSendAckBanner(props.fleetSendAck, props.onDismissFleetSendAck)}
        ${props.onChatSendOrchestrateAllChange ? renderFleetSendOptions(props) : nothing}
        ${renderFleetSendRoutingPreview(fleetRoutingPreview)}
        <div class="chat-fleet-footer">
          <div class="chat-fleet-footer__left">${renderComposerFooterPill(props, isBusy)}</div>
          <div class="chat-fleet-footer__right">
            <span
              class="chat-fleet-footer__latency ${props.connected ? "" : "chat-fleet-footer__latency--offline"}"
            >
              <span class="chat-fleet-footer__latency-dot" aria-hidden="true"></span>
              ${props.connected ? "Online" : "Offline"}
            </span>
            ${
              canAbort
                ? html`
                    <button
                      type="button"
                      class="chat-fleet-footer__btn chat-fleet-footer__btn--stop"
                      title="Stop the current agent run"
                      @click=${() => props.onAbort?.()}
                    >
                      Stop run
                    </button>
                  `
                : nothing
            }
            ${
              props.onNewSession && !canAbort
                ? html`
                    <button
                      type="button"
                      class="chat-fleet-footer__btn"
                      title="Start a new chat session"
                      @click=${props.onNewSession}
                    >
                      New session
                    </button>
                  `
                : nothing
            }
            ${renderChatComposerPlusMenu({
              open: props.chatComposerPlusOpen === true,
              focusedIndex: props.chatComposerPlusFocusIndex ?? -1,
              triggerRef: null,
              panelRef: null,
              sessionKey: props.sessionKey,
              queueMode: (props.fleetDefaultQueueMode === "collect" ? "collect" : "steer") as
                | "collect"
                | "steer",
              onQueueModeToggle: (next) => props.onQueueModeSelect?.(next),
              onOpenPalace: () => props.onToggleChatPalaceContinuity?.(),
              onOpenDraft: () => {
                const draftEl = document.querySelector(
                  "fleet-assist-draft.chat-compose__assist-hidden",
                ) as (HTMLElement & { open?: () => void }) | null;
                draftEl?.open?.();
              },
              onOpenControls: () => {
                const dock = document.querySelector("[data-testid='chat-composer-dock']");
                const dropdown = dock?.querySelector(
                  ".chat-compose__controls-dropdown",
                ) as HTMLElement | null;
                dropdown?.classList.remove("hidden");
              },
              // JAISIU-3588 (followup) — the `+` menu must work
              // regardless of existing draft text. Always insert
              // the trigger character at the end of the draft
              // (after a separator if needed) so the picker can
              // open against the new token, even mid-message.
              onOpenMention: () => {
                const next = appendMentionOrSlashTrigger(props.draft, "@");
                props.onDraftChange(next);
              },
              onOpenCommands: () => {
                const next = appendMentionOrSlashTrigger(props.draft, "/");
                props.onDraftChange(next);
              },
              onToggle: () => props.onToggleChatComposerPlus?.(),
              onClose: () => props.onCloseChatComposerPlus?.(),
              onFocusChange: (next) => props.onSetChatComposerPlusFocusIndex?.(next),
            })}
            <button
              type="button"
              class="chat-fleet-footer__btn"
              title="Scroll the message list to the fleet orchestration queue."
              @click=${scrollFleetQueue}
              aria-label="Scroll to fleet queue"
            >
              ${icons.list} Fleet queue
            </button>
          </div>
        </div>
      </div>
    </div>
  `;

  const renderFleetComposeDock = (placement: "inline" | "float") => {
    const waitActive = isChatWaitActive({
      sending: props.sending,
      canAbort: props.canAbort,
      stream: props.stream,
      streamStartedAt: props.streamStartedAt,
    });
    // Collect-mode banner: shown when the controller reports at least one
    // queued message. Independent of `waitActive` because the messages
    // can still be queued even while the live-run bubble is gone (e.g.,
    // very short-lived runs). Copy mirrors the per-bubble chip wording
    // so the two surfaces never disagree about what's happening.
    const queueDepth = Math.max(0, props.collectQueueDepth ?? 0);
    const collectBanner =
      queueDepth > 0
        ? html`<div
            class="chat-collect-banner"
            role="status"
            data-testid="chat-collect-banner"
            aria-live="polite"
          >
            <span class="chat-collect-banner__icon" aria-hidden="true">⏳</span>
            <span class="chat-collect-banner__text"
              >Queued${queueDepth > 1 ? ` (${queueDepth})` : ""} — the agent will reply when this
              turn closes</span
            >
          </div>`
        : nothing;
    const placementClass =
      placement === "inline" ? "chat-compose-inline-dock" : "chat-compose-float-dock";
    const placementProcessing = waitActive ? `${placementClass}--processing` : "";
    // JAISIU-3588 — the <aside> wrapper IS the shell. The shell class
    // plus chat-compose--fleet-dock live here so all dock-specific
    // CSS rules (textarea, action row, footer) match. The aside
    // itself is no longer a separate wrapper with its own border.
    const dockClassFull = [
      placementClass,
      placementProcessing,
      "chat-compose",
      "chat-compose--fleet",
      "chat-compose--fleet-dock",
      "chat-compose__shell",
    ]
      .filter(Boolean)
      .join(" ");
    return html`
      <aside
        class=${dockClassFull}
        data-testid="chat-composer-dock"
        data-processing=${waitActive ? "true" : "false"}
        role="region"
        aria-label="Message input"
        @dragover=${handleDragOver}
        @dragenter=${handleDragEnter}
        @dragleave=${handleDragLeave}
        @drop=${(e: DragEvent) => handleDrop(e, props)}
      >
        ${collectBanner}
        ${
          props.chatEditingPriorMessage
            ? html`
                <div class="chat-edit-pending-banner chat-edit-pending-banner--dock" role="status">
                  <span class="chat-edit-pending-banner__text"
                    >Editing a previous message — Send will ask to reset the thread from here.</span
                  >
                  ${
                    props.onCancelPendingMessageEdit
                      ? html`
                          <button
                            type="button"
                            class="btn subtle btn--sm chat-edit-pending-banner__cancel"
                            title=${FLEET_CHAT_EDIT_HINTS.cancelEdit}
                            @click=${props.onCancelPendingMessageEdit}
                          >
                            Cancel edit
                          </button>
                        `
                      : nothing
                  }
                </div>
              `
            : nothing
        }
        <div class="chat-compose__grow" data-testid="chat-composer-grow">
          ${renderChatPalaceContinuityDropdown({
            open: props.chatPalaceContinuityOpen === true,
            sessionKey: props.sessionKey,
            loading: props.chatPalaceContinuityLoading === true,
            clearing: props.chatPalaceContinuityClearing === true,
            mutating: props.chatPalaceContinuityMutating === true,
            pending: props.chatPalaceContinuityPending ?? 0,
            error: props.chatPalaceContinuityError ?? null,
            snapshot: props.chatPalaceContinuity ?? null,
            draft: props.chatPalaceContinuityDraft ?? "",
            draftCategory: props.chatPalaceContinuityDraftCategory ?? "fact",
            onClose: () => props.onCloseChatPalaceContinuity?.(),
            onRefresh: () => props.onRefreshChatPalaceContinuity?.(),
            onClearSession: () => props.onClearChatPalaceContinuity?.(),
            onRemovePoint: (pointId: string) => props.onRemoveChatPalaceContinuityPoint?.(pointId),
            onDraftChange: (draft: string) => props.onChatPalaceContinuityDraftChange?.(draft),
            onDraftCategoryChange: (category: "fact" | "decision") =>
              props.onChatPalaceContinuityDraftCategoryChange?.(category),
            onAddPoint: () => props.onAddChatPalaceContinuityPoint?.(),
          })}
          ${renderFleetComposeControlsGrowPanel(props)}
        </div>
          <!-- No accept filter. The store keys derivatives off the media type
               but never refuses on it, so a video, an archive, or a blob
               with no extension is a valid attachment. Narrowing the picker
               here is what produced "unsupported type: video/mp4" for a
               perfectly ordinary .mp4. -->
          <input
            type="file"
            class="chat-fleet-attach-input sr-only"
            multiple
            aria-hidden="true"
            tabindex="-1"
            @change=${(e: Event) => handleAttachInputChange(e, props)}
          />
          ${renderAttachmentPreview(props)}
          <!-- JAISIU-3588 — the [data-composer-grow-draft] slot +
               a visually-hidden <fleet-assist-draft> host live INSIDE
               the dock so the component's built-in slot resolver
               (closest('[data-testid=chat-composer-dock]')) finds
               the slot. The popover's Draft entry invokes the
               component's public .open() to render the picker into
               the slot. -->
          <div class="chat-compose__grow-draft" data-composer-grow-draft></div>
          <fleet-assist-draft
            class="chat-compose__assist-hidden"
            .composerDraft=${props.draft}
            .onInsert=${(draft: string) => props.onDraftChange(draft)}
            .onOpenChange=${(open: boolean) => {
              if (open) {
                document
                  .querySelectorAll(".chat-compose__controls-dropdown")
                  .forEach((el) => el.classList.add("hidden"));
                props.onCloseChatPalaceContinuity?.();
              }
            }}
            ?disabled=${!props.connected}
          ></fleet-assist-draft>
          ${
            slashOpen
              ? renderSlashCommandPicker({
                  commands: slashCommands,
                  query: slash.query,
                  activeIndex: slashIndex,
                  onSelect: commitSlashCommand,
                  onHoverIndex: (next) => props.onSlashCommandIndexChange?.(next),
                  // JAISIU-3588 (followup) — close button on the
                  // slash picker. Removes the trailing `/token`
                  // from the draft so the picker closes.
                  onClose: () =>
                    props.onDraftChange(stripTrailingSlashOrMentionToken(props.draft, "/")),
                })
              : mentionOpen
                ? renderMentionPicker({
                    agents: mentionAgents,
                    sessions: props.sessions,
                    query: mention.query,
                    activeIndex: mentionIndex,
                    onSelect: commitMention,
                    onHoverIndex: (next) => props.onMentionIndexChange?.(next),
                    // JAISIU-3588 (followup) — close button on the
                    // mention picker. Removes the trailing `@token`
                    // from the draft so the picker closes.
                    onClose: () =>
                      props.onDraftChange(stripTrailingSlashOrMentionToken(props.draft, "@")),
                  })
                : fileContextOpen
                  ? renderFileContextPicker({
                      tree: props.chatWorkspaceTree ?? [],
                      query: fileContext.query,
                      activeIndex: fileContextIndex,
                      onSelect: commitFileContext,
                      onHoverIndex: (next) => {
                        props.onChatFileContextIndexChange?.(next);
                      },
                    })
                  : folderContextOpen
                    ? renderFolderContextPicker({
                        tree: props.chatWorkspaceTree ?? [],
                        query: folderContext.query,
                        activeIndex: folderContextIndex,
                        onSelect: commitFolderContext,
                        onHoverIndex: (next) => {
                          props.onChatFolderContextIndexChange?.(next);
                        },
                      })
                    : nothing
          }
          
            <!-- JAISIU-3588 — chrome row is now minimal: just a
                 placeholder strip. Context, heartbeat, and the
                 composer Online/Offline status all moved out:
                   - Context → tools row (after Attach + +)
                   - Heartbeat → tools row (after Context, when active)
                   - Online/Offline → deleted (duplicate of the global
                     Jaisiu Health indicator in the top bar)
                 The textarea (data-testid=chat-composer-input) gets
                 to fill almost the entire dock area. -->
            <div class="chat-compose__chrome">
              <div class="chat-compose__chrome-left"></div>
              <div class="chat-compose__chrome-right"></div>
            </div>
            <div class="chat-compose__fleet-row chat-compose__fleet-row--dock">
              <label
                class="field chat-compose__field chat-compose__field--fleet chat-compose__field--fleet-dock"
                data-testid="chat-composer"
              >
                <span class="sr-only">Message</span>
                <textarea
                  data-testid="chat-composer-input"
                  ${ref((el: Element | undefined) => {
                    if (el) {
                      syncComposeTextarea(el as HTMLTextAreaElement, props.sessionKey, props.draft);
                    }
                  })}
                  ?disabled=${!props.connected}
                  @keydown=${(e: KeyboardEvent) => {
                    if (e.isComposing || e.keyCode === 229) {
                      return;
                    }
                    // JAISIU-3588 — Shift+Tab cycle of composer modes removed.
                    // Agent is the only mode in this composer; cycleFleetComposerMode
                    // was deleted because it had no other callers.
                    if (slashOpen) {
                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        props.onSlashCommandIndexChange?.(
                          Math.min(slashIndex + 1, slashMatches.length - 1),
                        );
                        return;
                      }
                      if (e.key === "ArrowUp") {
                        e.preventDefault();
                        props.onSlashCommandIndexChange?.(Math.max(slashIndex - 1, 0));
                        return;
                      }
                      if (e.key === "Tab" || (e.key === "Enter" && !e.shiftKey)) {
                        e.preventDefault();
                        const pick = slashMatches[slashIndex];
                        if (pick) {
                          commitSlashCommand(pick);
                          // Gateway-bound no-args commands are complete on pick: Enter
                          // should also send. Args-accepting commands insert a
                          // trailing space which closes the picker, and the next
                          // Enter sends. Local commands (e.g. /code) expand the
                          // draft and do not need a send. JAISIU-3094.
                          if (pick && shouldSendAfterSlashPick(pick)) {
                            queueMicrotask(() => handleComposerSend());
                          }
                        }
                        return;
                      }
                      if (e.key === "Escape") {
                        e.preventDefault();
                        props.onDraftChange("");
                        return;
                      }
                    }
                    if (fileContextOpen) {
                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        props.onChatFileContextIndexChange?.(
                          Math.min(fileContextIndex + 1, fileContextMatches.length - 1),
                        );
                        return;
                      }
                      if (e.key === "ArrowUp") {
                        e.preventDefault();
                        props.onChatFileContextIndexChange?.(Math.max(fileContextIndex - 1, 0));
                        return;
                      }
                      if (e.key === "Tab" || (e.key === "Enter" && !e.shiftKey)) {
                        e.preventDefault();
                        const pick = fileContextMatches[fileContextIndex];
                        if (pick) {
                          commitFileContext(pick.path);
                        }
                        return;
                      }
                    }
                    if (mentionOpen) {
                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        props.onMentionIndexChange?.(
                          Math.min(mentionIndex + 1, mentionMatches.length - 1),
                        );
                        return;
                      }
                      if (e.key === "ArrowUp") {
                        e.preventDefault();
                        props.onMentionIndexChange?.(Math.max(mentionIndex - 1, 0));
                        return;
                      }
                      if (e.key === "Tab" || (e.key === "Enter" && !e.shiftKey)) {
                        e.preventDefault();
                        const pick = mentionMatches[mentionIndex];
                        if (pick) {
                          commitMention(pick.id);
                        }
                        return;
                      }
                    }
                    if (e.key === "Escape" && canAbort && !slashOpen) {
                      e.preventDefault();
                      props.onAbort?.();
                      return;
                    }
                    if (e.key !== "Enter") {
                      return;
                    }
                    if (e.shiftKey) {
                      return;
                    }
                    if (!props.connected) {
                      return;
                    }
                    e.preventDefault();
                    if (canCompose && canSubmit) {
                      handleComposerSend();
                    }
                  }}
                  @input=${(e: Event) => {
                    const target = e.target as HTMLTextAreaElement;
                    adjustTextareaHeight(target);
                    props.onDraftChange(target.value);
                  }}
                  @paste=${(e: ClipboardEvent) => handlePaste(e, props)}
                  placeholder=${composePlaceholder}
                ></textarea>
              </label>
            </div>
            <div class="chat-compose__actions chat-compose__actions--fleet">
              <div class="chat-compose__tools">
                ${
                  // JAISIU-3297 UX-R8 (operator feedback 2026-09-14):
                  // Stop control was removed from the composer toolbar.
                  // It now lives only on the chat-send-button slot, which
                  // morphs into a Stop control while a run is in flight
                  // (see the send-button block below). Shift-click on that
                  // unified control triggers the destructive revert via
                  // props.onStopThinking. No second Stop affordance is
                  // rendered here — sending is the stop.
                  nothing
                }
                <button
                  type="button"
                  class="chat-compose__tool"
                  title="Attach files, images, video, or documents to the next message."
                  aria-label="Attach files"
                  ?disabled=${!props.connected || !props.onAttachmentsChange}
                  @click=${(e: MouseEvent) => {
                    const root = (e.currentTarget as HTMLElement).closest(".chat-compose");
                    root?.querySelector<HTMLInputElement>(".chat-fleet-attach-input")?.click();
                  }}
                >
                  ${icons.paperclip} Attach
                </button>
                ${renderChatComposerPlusMenu({
                  open: props.chatComposerPlusOpen === true,
                  focusedIndex: props.chatComposerPlusFocusIndex ?? -1,
                  triggerRef: null,
                  panelRef: null,
                  sessionKey: props.sessionKey,
                  queueMode: (props.fleetDefaultQueueMode === "collect" ? "collect" : "steer") as
                    | "collect"
                    | "steer",
                  onQueueModeToggle: (next) => props.onQueueModeSelect?.(next),
                  onOpenPalace: () => props.onToggleChatPalaceContinuity?.(),
                  onOpenDraft: () => {
                    const draftEl = document.querySelector(
                      "fleet-assist-draft.chat-compose__assist-hidden",
                    ) as (HTMLElement & { open?: () => void }) | null;
                    draftEl?.open?.();
                  },
                  onOpenControls: () => {
                    const dock = document.querySelector("[data-testid='chat-composer-dock']");
                    const dropdown = dock?.querySelector(
                      ".chat-compose__controls-dropdown",
                    ) as HTMLElement | null;
                    dropdown?.classList.remove("hidden");
                  },
                  // JAISIU-3588 (followup) — the `+` menu must work
                  // regardless of existing draft text. Append the
                  // trigger character at the end of the draft so
                  // the picker can open against the new token,
                  // even mid-message.
                  onOpenMention: () => {
                    const next = appendMentionOrSlashTrigger(props.draft, "@");
                    props.onDraftChange(next);
                  },
                  onOpenCommands: () => {
                    const next = appendMentionOrSlashTrigger(props.draft, "/");
                    props.onDraftChange(next);
                  },
                  onToggle: () => props.onToggleChatComposerPlus?.(),
                  onClose: () => props.onCloseChatComposerPlus?.(),
                  onFocusChange: (next) => props.onSetChatComposerPlusFocusIndex?.(next),
                })}
                <button
                  type="button"
                  class="chat-compose__context-chip"
                  title=${
                    props.sending
                      ? "Context fill updates after the agent finishes this reply."
                      : contextUsage.limit
                        ? `Context: ${contextUsage.label} (${contextUsage.percent ?? 0}%) — click for breakdown`
                        : "Context usage — click for breakdown"
                  }
                  aria-label="Context usage breakdown"
                  data-testid="chat-composer-context-chip"
                  ?disabled=${!props.connected}
                  @click=${() => props.onOpenChatContextPanel?.()}
                >
                  ${renderContextUsageRing(contextUsage.percent, { size: 14 })}
                  <span
                    >Context
                    ${contextUsage.percent != null ? `${contextUsage.percent}%` : contextUsage.label}</span
                  >
                </button>
                ${
                  heartbeatActive
                    ? html`
                        <span
                          class="chat-compose__heartbeat"
                          role="status"
                          aria-live="polite"
                          title="Agent heartbeat OK — last turn was a quick ack, not a full reply"
                        >
                          ${icons.heartFilled}
                          <span class="sr-only">Agent heartbeat OK</span>
                        </span>
                      `
                    : nothing
                }
                ${renderComposerStatusSlot(props, isBusy)}
              </div>
              <button
                type="button"
                class=${canAbort ? "chat-compose__send chat-compose__send--stop" : "chat-compose__send"}
                data-testid="chat-send-button"
                ?disabled=${!canAbort && !canSubmit}
                title=${
                  canAbort
                    ? canStopThinking
                      ? "Stop the current agent run. Shift-click to also revert code changes from this prompt."
                      : "Stop the current agent run (Escape)"
                    : attachmentsUploading
                      ? "Waiting for every attached file to finish storing."
                      : isBusy
                        ? props.fleetDefaultQueueMode === "steer" || !props.fleetDefaultQueueMode
                          ? "Redirect: steer the live run with this message."
                          : "Queue this message until the current run finishes."
                        : "Send this message to the agent now."
                }
                aria-label=${
                  canAbort
                    ? canStopThinking
                      ? "Stop agent run (Shift-click to revert code changes)"
                      : "Stop agent run"
                    : isBusy &&
                        (props.fleetDefaultQueueMode === "steer" || !props.fleetDefaultQueueMode)
                      ? "Redirect message"
                      : isBusy
                        ? "Queue message"
                        : "Send message"
                }
                @click=${(e: MouseEvent) => {
                  if (canAbort) {
                    if (e.shiftKey && canStopThinking) {
                      props.onStopThinking?.();
                    } else {
                      props.onAbort?.();
                    }
                    return;
                  }
                  handleComposerSend();
                }}
              >
                ${
                  canAbort
                    ? html`
                        ${icons.power} Stop
                        ${
                          canStopThinking
                            ? html`<span
                                class="chat-compose__send-hint"
                                aria-hidden="true"
                                title="Shift-click to also revert code changes from this prompt"
                                >⇧</span
                              >`
                            : nothing
                        }
                      `
                    : isBusy &&
                        (props.fleetDefaultQueueMode === "steer" || !props.fleetDefaultQueueMode)
                      ? "Redirect"
                      : isBusy
                        ? "Queue"
                        : "Send"
                }
                ${canAbort ? nothing : icons.send}
              </button>
            </div>
            ${renderChatFleetDockFooter(props, { canAbort, isBusy, onScrollFleetQueue: scrollFleetQueue })}
            ${
              // JAISIU-3503 — transient "Run stopped" confirmation.
              // Renders as an aria-live polite status line at the
              // bottom of the composer for a few seconds after a
              // Stop-button / /stop / Stop-run abort lands, so the
              // operator gets explicit feedback the abort fired
              // (without it, the cleared textarea reads as "nothing
              // happened"). Cleared by the host on the next draft
              // change so it never lingers past the next prompt.
              props.chatStopToast
                ? html`
                    <div
                      class="chat-compose__stop-toast"
                      role="status"
                      aria-live="polite"
                      data-testid="chat-stop-toast"
                    >
                      ${icons.power}
                      <span>${props.chatStopToast}</span>
                    </div>
                  `
                : nothing
            }
        ${nothing}
        </div>
      </aside>
    `;
  };

  /** Command Center controls: queue/routing/send options — composer lives in float or inline dock. */
  const fleetControlsPanel = html`
    <div class="chat-send-pane__scroll chat-send-pane__scroll--workspace">
      ${fleetV2SendPaneChrome} ${fleetComposeScrollChrome}
    </div>
  `;

  const fleetV2RunContextPanel = html`
    <div class="chat-run-context-pane">
      <div class="chat-run-context-pane__scroll">${fleetV2RunContextPane}</div>
    </div>
  `;

  // The Inspector "Files" tab absorbs the legacy "Projects" tab — see
  // fleet-workspace-presets.ts. We stack (1) artifact-tracked file
  // changes the agent made via write/edit tools, (2) the live workspace
  // tree for the active session/project, and (3) the projects list so
  // operators can manage project folders from the same view.
  const showFilesBrowser = props.onChatWorkspaceRefresh && props.onChatWorkspaceOpenFile;
  const filesPanel = showFilesBrowser
    ? html`
        <div class="session-inspector-files-combined">
          ${
            props.onRefreshSessionArtifacts
              ? renderFleetSessionInspectorFilesTab({
                  loading: props.sessionArtifactsLoading === true,
                  error: props.sessionArtifactsError ?? null,
                  changes: props.sessionArtifacts ?? [],
                  selectedChangeId: props.sessionArtifactSelectedId ?? null,
                  detailLoading: props.sessionArtifactDetailLoading === true,
                  detail: props.sessionArtifactDetail ?? null,
                  connected: props.connected,
                  tree: (props.chatWorkspaceTree ??
                    []) as import("../controllers/chat-workspace.ts").WorkspaceTreeNode[],
                  selectedPath: props.chatWorkspaceSelectedPath ?? null,
                  fileContent: props.chatWorkspaceFileContent ?? null,
                  fileLoading: props.chatWorkspaceFileLoading === true,
                  editContent: props.sessionArtifactEditContent ?? "",
                  writeSaving: false,
                  onRefresh: () => props.onRefreshSessionArtifacts?.(),
                  onSelectChange: (id) => props.onSelectSessionArtifact?.(id),
                  onSelectFile: (path) => props.onChatWorkspaceOpenFile?.(path),
                  onEditContentChange: (content) =>
                    props.onSessionArtifactEditContentChange?.(content),
                  onSaveFile: () => props.onSaveSessionWorkspaceFile?.(),
                  // Inbound attachments live above the project-diff list.
                  // Kept separate because an attach changes no project file.
                  uploads: props.sessionUploads
                    ? {
                        loading: props.sessionUploads.loading,
                        error: props.sessionUploads.error,
                        files: props.sessionUploads.files,
                        connected: props.connected,
                        resolveUrl: (file, variant) =>
                          props.resolveSessionFileVariantUrl?.(file, variant) ?? "",
                        onRefresh: () => props.onRefreshSessionUploads?.(),
                        onOpenInTranscript: props.onSessionUploadJump
                          ? (file) => props.onSessionUploadJump?.(file.fileId)
                          : undefined,
                      }
                    : undefined,
                })
              : nothing
          }
          ${renderFleetProjectFilesTab({
            sessionKey: props.sessionKey,
            sessionProjects,
            connected: props.connected,
            loading: props.chatWorkspaceLoading ?? false,
            error: props.chatWorkspaceError ?? null,
            workspacePath: props.chatWorkspacePath ?? null,
            scope: props.chatWorkspaceScope ?? null,
            truncated: props.chatWorkspaceTruncated ?? false,
            tree: (props.chatWorkspaceTree ??
              []) as import("../chat/fleet-session-files-tree.ts").FleetFilesTreeNode[],
            expandedDirs: props.chatWorkspaceExpandedDirs ?? {},
            selectedPath: props.chatWorkspaceSelectedPath ?? null,
            fileLoading: props.chatWorkspaceFileLoading ?? false,
            fileError: props.chatWorkspaceFileError ?? null,
            fileContent: props.chatWorkspaceFileContent ?? null,
            fileTruncated: props.chatWorkspaceFileTruncated ?? false,
            onRefresh: () => props.onChatWorkspaceRefresh?.(),
            onToggleDir: (path) => props.onChatWorkspaceToggleDir?.(path),
            onOpenFile: (path) => props.onChatWorkspaceOpenFile?.(path),
            onInsertIntoComposer: props.onInsertIntoComposer,
          })}
          ${
            props.onFleetProjectsRefresh && props.onFleetProjectOpen
              ? renderFleetProjectsPanelBody({
                  loading: props.fleetProjectsLoading ?? false,
                  error: props.fleetProjectsError ?? null,
                  projects: sessionProjects,
                  activeProjectId: props.chatActiveProjectId ?? null,
                  connected: props.connected,
                  onRefresh: () => props.onFleetProjectsRefresh?.(),
                  onDiscover: () => props.onFleetProjectsDiscover?.(),
                  onCreate: (name) => props.onFleetProjectCreate?.(name),
                  onOpen: (project) => props.onFleetProjectOpen?.(project),
                  onRename: (project, name) => props.onFleetProjectRename?.(project, name),
                  onDelete: (project) => props.onFleetProjectDelete?.(project),
                })
              : nothing
          }
        </div>
      `
    : html` <p class="muted">File browser unavailable.</p> `;

  const workspacePanelOpen = props.fleetWorkspacePanelOpen !== false;

  const chatHistoryProps: FleetChatHistorySidebarProps | null = props.onChatHistorySelectSession
    ? {
        collapsed: props.fleetChatHistoryCollapsed === true,
        // JAISIU-4497 — base path for the "open in a new browser tab"
        // deep link built by the sidebar.
        basePath: props.basePath ?? "",
        onToggleCollapsed: () => props.onToggleFleetChatHistoryCollapsed?.(),
        loading: props.chatHistoryLoading === true,
        connected: props.connected,
        searchQuery: props.chatHistorySearchQuery ?? "",
        // JAISIU-3655 (followup) — "Search in activities" toggle.
        // Shared with the in-thread search toggle (both flows
        // read/write the same boolean via the host's
        // `chatMessageSearchInActivities` state).
        searchInActivities: props.chatHistorySearchInActivities === true,
        onSearchInActivitiesChange: (next) => props.onChatHistorySearchInActivitiesChange?.(next),
        showArchived: props.chatHistoryShowArchived === true,
        groups: props.chatHistoryGroups ?? [],
        archived: props.chatHistoryArchived ?? [],
        activeSessionKey: props.sessionKey,
        canArchiveActive: props.chatHistoryCanArchiveActive === true,
        // Shared "now" for the relative-time strings in the listing. Re-using
        // the live wait ticker (or Date.now as a fallback) means all entries
        // resolve timestamps against a single instant per render and the
        // memoized sidebar caches a stable template until the minute bucket
        // changes.
        nowMs: props.chatWaitNow,
        onSearchQueryChange: (next) => props.onChatHistorySearchQueryChange?.(next),
        // JAISIU-3651 — body index flows in from the host so the sidebar
        // search can also match loaded message bodies for any chat in
        // `app.chatMessagesBySession`. Omitted when null to keep the
        // title+preview-only behaviour intact.
        loadedMessageTextBySession: props.chatHistoryLoadedMessageIndex ?? undefined,
        // JAISIU-3653 — pass the jump resolver through so the sidebar
        // click surfaces a `jumpToMessageId` alongside the session
        // key. Without an active query (or below the min-length)
        // the resolver returns `null` and the click falls back to
        // the plain session-open flow (unchanged).
        resolveJumpToMessageId: props.resolveChatHistoryJumpToMessageId,
        onToggleShowArchived: () => props.onToggleChatHistoryShowArchived?.(),
        // JAISIU-3500: preview visibility (off by default).
        showPreviews: props.chatHistoryShowPreviews === true,
        onToggleShowPreviews: () => props.onToggleChatHistoryShowPreviews?.(),
        onNewChat: () => props.onChatHistoryNewChat?.(),
        onArchiveAndNew: () => props.onChatHistoryArchiveAndNew?.(),
        onSelectSession: (key, opts) => {
          props.onFleetChatMainTabChange?.("chat");
          props.onChatHistorySelectSession?.(key, opts);
        },
        onArchiveSession: (key) => props.onChatHistoryArchiveSession?.(key),
        onRestoreSession: (key) => props.onChatHistoryRestoreSession?.(key),
        onDeleteSession: (key) => props.onChatHistoryDeleteSession?.(key),
        onRenameSession: (key, title) => props.onChatHistoryRenameSession?.(key, title),
        onRefresh: () => props.onChatHistoryRefresh?.(),
        // JAISIU-3327 / UX-R2 — bulk-select plumbing (wire-through).
        selectMode: props.chatHistorySelectMode === true,
        selectedKeys: props.chatHistorySelectedKeys,
        allSelectableKeys: props.chatHistoryAllSelectableKeys,
        onToggleSelectMode: () => props.onToggleChatHistorySelectMode?.(),
        onToggleKeySelected: (key) => props.onToggleChatHistoryKeySelected?.(key),
        onSelectAllKeys: () => props.onSelectAllChatHistoryKeys?.(),
        onClearSelection: () => props.onClearChatHistorySelection?.(),
        onBulkDeleteSelected: () => props.onBulkDeleteSelectedChatHistory?.(),
        onBulkArchiveSelected: () => props.onBulkArchiveSelectedChatHistory?.(),
        // JAISIU-3328 / UX-R3 — favorite (pinned) chats plumbing.
        favoriteKeys: props.chatHistoryFavoriteKeys,
        onToggleFavorite: (key) => props.onToggleChatHistoryFavorite?.(key),
      }
    : null;

  // Session navigator props (top bar moved into Inspector → Agents →
  // Sessions, see fleet-v2-agent-directory.ts renderSessionsTab).
  const sessionBarProps = showSessionTabs
    ? {
        activeSessionKey: props.sessionKey,
        openTabs: props.chatOpenTabs!,
        sessions: props.sessions,
        labelHints: tabLabelHints,
        tabMeta: props.chatTabMeta,
        highlightSessionKey: props.chatTabHighlightKey ?? null,
        tabActivity: props.sessionTabActivity,
        connected: props.connected,
        onSelectTab: props.onSelectChatTab!,
        onCloseTab: props.onCloseChatTab!,
        onNewEmptySession: props.onNewChatTab!,
        onArchiveAndNew: props.onChatHistoryArchiveAndNew,
        onForkTab: props.onForkChatTab,
        onResetTab: props.onResetChatTab,
        onDeleteSession: props.onDeleteChatSession,
        onManageSessions: props.onManageSessions,
        onShowArchivedSessions: props.onShowArchivedSessions,
        onNewAgent: props.onNewAgent,
        onOpenAgents: props.onOpenAgents,
        sessionChromeCollapsed: props.fleetSessionChromeCollapsed,
        onToggleSessionChromeCollapsed: props.onToggleFleetSessionChromeCollapsed,
        sessionNavigatorScrollable: props.fleetSessionNavigatorScrollable,
        onToggleSessionNavigatorScrollable: props.onToggleFleetSessionNavigatorScrollable,
        sessionNavigatorCanBack: props.sessionNavigatorCanBack,
        sessionNavigatorCanForward: props.sessionNavigatorCanForward,
        onSessionNavigatorBack: props.onSessionNavigatorBack,
        onSessionNavigatorForward: props.onSessionNavigatorForward,
        resetNodeBusy: props.fleetResetNodeBusy,
        onResetNode: props.onFleetResetNode,
      }
    : null;

  const sessionNavigatorScrollable = props.fleetSessionNavigatorScrollable !== false;
  const workspacePreset = props.fleetWorkspacePreset ?? "unified";
  const composerInline = workspacePreset === "operator" || workspacePreset === "unified";

  // Top bar → inspector (JAISIU-3112). The session navigator (history
  // arrows, Reset Node, scrollable toggle, open tabs, session catalog)
  // is no longer rendered above the chat transcript in any preset. It
  // lives inside Inspector → Agents → Sessions for every preset
  // (users on commandCenter / monitor can toggle the inspector back on
  // via the existing per-preset panel toggle).
  //
  // `sessionNavigatorScrollable` is preserved because it still controls
  // the bar's *internal* layout (collapsed vs scrollable column).

  // JAISIU-3094: removed the inline `fleetV2RunContextPanel` from the top of
  // the transcript. The full context-window panel (ring + bar + legend) was
  // being rendered both here and inside Inspector → Health, covering the
  // top half of the chat transcript with a duplicate of the inspector view.
  // The transcript now shows only the conversation; the context panel
  // remains accessible via Inspector → Health and the analytics stack.
  const fleetTranscriptPanel = html`
    <div class="fleet-chat-tab-layout fleet-v2-transcript-layout">
      <div class="fleet-chat-tab-main fleet-chat-tab-main--thread-only">
        <div class="chat-conversation-pane chat-conversation-pane--thread">
          ${fleetV2ThreadPane}
        </div>
        ${renderChatStatusStrip({ items: chatStatusItems })}
        ${composerInline ? renderFleetComposeDock("inline") : nothing}
      </div>
    </div>
  `;

  const directoryPanel = renderFleetV2AgentDirectory({
    activeTab: props.fleetV2DirectoryTab ?? "map",
    onTabChange: (tab) => props.onFleetV2DirectoryTabChange?.(tab),
    connected: props.connected,
    agents: props.agents ?? [],
    subagents: subagentCards,
    // UX-PLAN Pillar D — pass the same snapshot down so the inspector
    // delegation map reads from one shared derived value.
    sessionSnapshot: props.chatSessionSnapshot,
    parentSessionKey: props.sessionKey,
    sessions: props.sessions,
    projects: sessionProjects,
    onOpenAgentsTab: props.onOpenAgentsTab,
    onOpenSubagentSession: props.onOpenSubagentSession,
    onOpenProjectSession: (project) => props.onFleetProjectOpen?.(project),
    onEditRoutes: props.onFleetGraphEditRoutes,
    sessionBar: sessionBarProps,
  });
  const analyticsPanel = renderFleetV2AnalyticsPanel({
    activeTab: props.fleetV2AnalyticsTab ?? "activity",
    onTabChange: (tab) => props.onFleetV2AnalyticsTabChange?.(tab),
    connected: props.connected,
    sending: props.sending,
    stream: props.stream,
    sessions: props.sessions,
    subagents: subagentCards,
    fleetTodos: props.fleetTodos?.length ?? 0,
    resetNodeBusy: props.fleetResetNodeBusy,
    onResetNode: props.onFleetResetNode,
    activity: props.onChatActivityTabChange
      ? {
          tab: props.chatActivityTab ?? "all",
          onTabChange: (tab) => props.onChatActivityTabChange?.(tab),
          searchQuery: props.chatActivitySearchQuery,
          onSearchChange: props.onChatActivitySearchChange,
          messages: props.messages ?? [],
          chatToolMessages: props.toolMessages ?? [],
          stream: props.stream,
          reasoningStream: props.reasoningStream,
          subagents: sessionChildren(props),
          showThinking: true,
          runActive,
          liveRunHint: props.liveRunHint,
          activeFileToolPath: props.chatActiveFileToolPath,
          fleetTodos: props.fleetTodos ?? [],
          connected: props.connected,
          agentId: activityAgentId,
          onEditAgentConfig: props.onEditAgentConfig,
          onOpenAgentsTab: props.onOpenAgentsTab,
          onOpenSubagentSession: (childKey, label) =>
            props.onOpenSubagentSession?.({
              childKey,
              parentKey: props.sessionKey,
              label,
            }),
        }
      : undefined,
  });

  const workspaceLayout = props.fleetWorkspaceLayout;
  const workspaceDensity = props.fleetWorkspaceDensity ?? "comfortable";

  const analyticsHealthPanel = renderFleetV2AnalyticsPanel({
    activeTab: "health",
    hideTabStrip: true,
    onTabChange: (tab) => props.onFleetV2AnalyticsTabChange?.(tab),
    connected: props.connected,
    sending: props.sending,
    stream: props.stream,
    sessions: props.sessions,
    subagents: subagentCards,
    fleetTodos: props.fleetTodos?.length ?? 0,
    resetNodeBusy: props.fleetResetNodeBusy,
    onResetNode: props.onFleetResetNode,
    ...trustActivity,
    runTimeline: trustActivity.runTimeline,
    fleetStats: props.fleetStats ?? null,
    events: {
      scopeSessionKey: props.eventsScopeSessionKey ?? props.sessionKey,
      eventsBySession: props.gatewayEventsBySession ?? {},
      eventsFlat: props.gatewayEvents,
      limit: 10,
    },
  });

  const analyticsActivityPanel = renderFleetV2AnalyticsPanel({
    activeTab: "activity",
    hideTabStrip: true,
    onTabChange: (tab) => props.onFleetV2AnalyticsTabChange?.(tab),
    connected: props.connected,
    sending: props.sending,
    stream: props.stream,
    sessions: props.sessions,
    subagents: subagentCards,
    fleetTodos: props.fleetTodos?.length ?? 0,
    resetNodeBusy: props.fleetResetNodeBusy,
    onResetNode: props.onFleetResetNode,
    activity: props.onChatActivityTabChange
      ? {
          tab: props.chatActivityTab ?? "all",
          onTabChange: (tab) => props.onChatActivityTabChange?.(tab),
          searchQuery: props.chatActivitySearchQuery,
          onSearchChange: props.onChatActivitySearchChange,
          messages: props.messages ?? [],
          chatToolMessages: props.toolMessages ?? [],
          stream: props.stream,
          reasoningStream: props.reasoningStream,
          subagents: sessionChildren(props),
          showThinking: true,
          runActive,
          liveRunHint: props.liveRunHint,
          activeFileToolPath: props.chatActiveFileToolPath,
          fleetTodos: props.fleetTodos ?? [],
          connected: props.connected,
          ...trustActivity,
          agentId: activityAgentId,
          onEditAgentConfig: props.onEditAgentConfig,
          onOpenAgentsTab: props.onOpenAgentsTab,
          onOpenSubagentSession: (childKey, label) =>
            props.onOpenSubagentSession?.({
              childKey,
              parentKey: props.sessionKey,
              label,
            }),
        }
      : undefined,
    events: {
      scopeSessionKey: props.eventsScopeSessionKey ?? props.sessionKey,
      eventsBySession: props.gatewayEventsBySession ?? {},
      eventsFlat: props.gatewayEvents,
      limit: 12,
    },
  });

  const inspectorPanel = workspaceLayout
    ? renderFleetInspectorPanel({
        activeTab: props.fleetInspectorTab ?? "agents",
        onTabChange: (tab) => props.onFleetInspectorTabChange?.(tab),
        panels: {
          agents: directoryPanel,
          execution: renderFleetExecutionPipeline({
            model: buildFleetExecutionView({
              subagents: sessionChildren(props),
              todosDone: (props.fleetTodos ?? []).filter((todo) => todo.status === "completed")
                .length,
              todosTotal: props.fleetTodos?.length,
            }),
            expandedId: props.fleetExecutionExpandedId ?? null,
            onToggle: (id) => props.onFleetExecutionToggle?.(id),
          }),
          activity: analyticsActivityPanel,
          health: html`
            <div class="fleet-inspector-health">
              ${fleetV2RunContextPanel} ${analyticsHealthPanel}
            </div>
          `,
          files: filesPanel,
          subagents: renderFleetSubagentActivityPanel({
            sessionKey: props.sessionKey,
            subagents: sessionChildren(props),
            filter: props.chatSubagentFilter ?? "all",
            subagentExpanded: props.chatSubagentExpanded ?? {},
            onToggleSubagentExpand: (id) => props.onToggleSubagentExpand?.(id),
            onFilterChange: (next) => props.onSubagentFilterChange?.(next),
            onCollapseAll: () => props.onCollapseAllSubagents?.(),
            onJumpLatestRunning: props.onJumpLatestRunning,
            hasRunning: sessionChildren(props).some(
              (c) => resolveSubagentActivityPhase(c) === "ongoing",
            ),
            onOpenSubagentSession: props.onOpenSubagentSession,
            otherSessionBuckets: props.otherSubagentBuckets ?? [],
            selectedParentId: props.selectedSubagentParentId ?? null,
            onSelectParent: (parentId) => props.onSelectSubagentParent?.(parentId),
          }),
          // Per-session, per-host tunnel health. The panel paints its
          // own empty state when there are no records, so the operator
          // always sees the inspector tab is wired.
          //
          // `TunnelHealthController` keeps a Map keyed by tunnel key
          // (`Record<string, TunnelRecord>`), while the panel consumes an
          // array — it calls `records.length` and `records.map`. Passing the
          // record straight through made both `undefined` and threw on the
          // Tunnels tab, so it is converted here.
          tunnels: renderTunnelHealthPanel({
            records: props.tunnelRecords ? Object.values(props.tunnelRecords) : [],
            activeSessionKey: props.sessionKey,
            onReconnect: (key) => props.onTunnelReconnect?.(key),
            onForget: props.onTunnelForget ? (key) => props.onTunnelForget?.(key) : undefined,
          }),
        },
      })
    : nothing;

  const fleetUnifiedLeftPanel = html`
    <div class="fleet-unified-left">
      <div class="fleet-unified-left__chat">
        <div class="fleet-chat-tab-layout fleet-v2-transcript-layout">
          <div class="fleet-chat-tab-main fleet-chat-tab-main--thread-only">
            <div
              class="chat-conversation-pane chat-conversation-pane--thread fleet-unified-left__thread"
            >
              ${fleetV2ThreadPane}
            </div>
          </div>
        </div>
      </div>
      <div class="fleet-unified-left__controls">
        ${renderChatStatusStrip({ items: chatStatusItems })} ${fleetV2SendPaneChrome}
        ${renderFleetComposeDock("inline")}
      </div>
    </div>
  `;

  const distractionExitBanner =
    assistDisplay === "minimal"
      ? html`
          <button
            type="button"
            class="fleet-v2-panel__action"
            title="Exit distraction-free mode"
            style="margin: 8px 12px 0; width: calc(100% - 24px); background: var(--accent, #6ea886); color: #fff; border-radius: 6px;"
            @click=${() => props.onSetAssistDisplay("on")}
          >
            Show Full Output
          </button>
        `
      : nothing;

  const stackedAnalyticsPanel = html`
    <div class="fleet-workspace-analytics-stack">
      ${distractionExitBanner} ${fleetV2RunContextPanel} ${analyticsPanel}
    </div>
  `;

  const distractionExitAction =
    assistDisplay === "minimal"
      ? html`
          <button
            type="button"
            class="fleet-v2-panel__action"
            title="Exit distraction-free mode"
            @click=${() => props.onSetAssistDisplay("on")}
          >
            Show Full Output
          </button>
        `
      : nothing;

  // JAISIU-3588 — keep Reset Node in the inspector "Run context &
  // analytics" panel; the operator only asked to remove it from
  // the unified-left composer panel.
  const resetNodeAction = props.onFleetResetNode
    ? html`
        <button
          type="button"
          class="fleet-v2-panel__action fleet-v2-panel__action--danger"
          ?disabled=${!props.connected || props.fleetResetNodeBusy}
          title="Stop lingering subagents and stuck runs on this gateway"
          @click=${props.onFleetResetNode}
        >
          ${props.fleetResetNodeBusy ? "Resetting…" : "Reset Node"}
        </button>
      `
    : nothing;

  // JAISIU-3603 followup — derive `sidePanelOpen` from
  // `props.fleetUnifiedDrawerOpen` once per chat render. The previous
  // inline ternary was deeply nested inside a conditional template,
  // and after a chat session switch Lit's diff could miss the
  // dependency edge for `props.fleetUnifiedDrawerOpen` (the icon
  // and `data-inspector-open` attribute updated but the right
  // column stayed in the DOM). Earlier still, the const was wrapped
  // in `live(...)` — `live()` returns a directive wrapper object, not
  // a primitive boolean, so Lit's diff compared object references and
  // never re-applied the `?hidden` binding on the right column. The
  // plain-const form below restores the primitive boolean and makes
  // the `?hidden=${!props.sidePanelOpen}` binding flip on every
  // `renderChat` call — including the re-render that follows a chat
  // session switch.
  const sidePanelOpen =
    Boolean(workspaceLayout) &&
    props.fleetUnifiedDrawerOpen !== false &&
    workspaceLayout?.visiblePanels.inspector !== false;

  const fleetSectionsStack = html`
    <div
      class="fleet-chat-workspace fleet-chat-workspace--${workspacePreset} fleet-chat-workspace--density-${workspaceDensity}"
    >
      ${
        errorDisplay
          ? errorDisplay.guardrail
            ? renderChatGuardrailBanner({
                draftMessage: props.draft ?? "",
                attachmentCount: props.attachments?.length ?? 0,
                finding: errorDisplay.guardrail,
                onTruncateRetry: props.onGuardrailTruncateRetry
                  ? (direction, trimmed) => {
                      // `direction` is advisory; the host just gets the
                      // trimmed string. The banner button label already
                      // makes the choice obvious to the user.
                      props.onGuardrailTruncateRetry?.(trimmed);
                    }
                  : undefined,
                onDismiss: props.onDismissError,
              })
            : html`
                <div class="callout danger chat-error-banner fleet-chat-workspace__banner">
                  <div class="chat-error-banner__title">${errorDisplay.title}</div>
                  <div class="chat-error-banner__detail">${errorDisplay.detail}</div>
                  ${
                    errorDisplay.recover
                      ? html`<p class="muted chat-error-banner__recover">
                          ${errorDisplay.recover}
                        </p>`
                      : nothing
                  }
                  ${
                    props.onDismissError
                      ? html`
                          <button
                            type="button"
                            class="btn subtle btn--sm"
                            @click=${() => props.onDismissError?.()}
                          >
                            Dismiss
                          </button>
                        `
                      : nothing
                  }
                </div>
              `
          : nothing
      }
      ${renderCompactionIndicator(props.compactionStatus)}
      ${
        workspaceLayout
          ? workspacePreset === "unified"
            ? renderFleetUnifiedLayout({
                layout: workspaceLayout,
                density: workspaceDensity,
                sideTab: props.fleetInspectorTab ?? "agents",
                sidePanelOpen,
                panels: {
                  left: fleetUnifiedLeftPanel,
                  right: inspectorPanel,
                },
                // JAISIU-3081: pass the persisted inspector width (or 0 to
                // indicate "use preset default") so the inspector column
                // grows/shrinks to match the operator's preference.
                inspectorWidth: props.fleetInspectorWidth ?? 0,
                // JAISIU-3082 (operator tweak msg 1a22b312): render the
                // discrete Inspector width picker inside the right panel
                // header (next to the INSPECTOR title), not in the topbar.
                inspectorWidthPicker: {
                  selectedIndex: props.fleetInspectorWidth ?? 0,
                  onSelect: (idx) => props.onFleetInspectorResize?.(idx),
                  asMenu: true,
                },
                onSideTabChange: (tab) => props.onFleetInspectorTabChange?.(tab),
                onToggleSidePanel: () => props.onFleetUnifiedDrawerToggle?.(),
                // JAISIU-3081: pixel-based resize hook. The unified layout
                // renders a draggable divider when this is provided.
                onInspectorResize: props.onFleetInspectorResize,
                onColumnResize: (ratio) => {
                  if (!props.onFleetWorkspaceLayoutChange) {
                    return;
                  }
                  const r = Math.max(0.15, Math.min(0.85, ratio));
                  props.onFleetWorkspaceLayoutChange({
                    ...workspaceLayout,
                    columns: [1 - r, 0, r],
                    rows: [...workspaceLayout.rows] as [number, number],
                    visiblePanels: { ...workspaceLayout.visiblePanels },
                  });
                },
              })
            : renderFleetWorkspaceLayout({
                preset: workspacePreset,
                layout: workspaceLayout,
                density: workspaceDensity,
                // JAISIU-3633: thread `sidePanelOpen` (already ANDed
                // against `visiblePanels.inspector` and
                // `fleetUnifiedDrawerOpen` above) into the operator
                // preset's layout so the topbar Inspector toggle can
                // actually hide / show the Inspector panel. Without
                // this, the operator preset's Inspector was always
                // visible and the toggle was a no-op.
                sidePanelOpen,
                // JAISIU-3082 (operator tweak msg 2eff7eff): pass the
                // persisted Inspector width index to the layout so
                // --fleet-inspector-width is applied to the Inspector
                // panel flex-basis (and the chat column keeps its width).
                inspectorWidth: props.fleetInspectorWidth ?? 0,
                panels: {
                  transcript: fleetTranscriptPanel,
                  directory: directoryPanel,
                  controls: fleetControlsPanel,
                  analytics: stackedAnalyticsPanel,
                  inspector: inspectorPanel,
                },
                panelMeta: [
                  {
                    id: "transcript",
                    title: "Chat transcript",
                    // JAISIU-3080: drop the panel header (title, subtext,
                    // Latest action) to reclaim vertical space for the
                    // conversation.
                    hideHeader: true,
                  },
                  {
                    id: "directory",
                    title: "Agent directory",
                    description: "Delegation map and quick-open actions.",
                  },
                  {
                    id: "controls",
                    title: "Controls",
                    description: "Routing preview, queue, send options, and operator prompts.",
                    actions: html`
                      <button
                        type="button"
                        class="fleet-v2-panel__action"
                        title="Focus the message box"
                        @click=${() =>
                          document
                            .querySelector(
                              ".chat-compose-inline-dock textarea, .chat-compose-float-dock textarea",
                            )
                            ?.scrollIntoView({ behavior: "smooth", block: "nearest" })}
                      >
                        Focus input
                      </button>
                    `,
                  },
                  {
                    id: "analytics",
                    title: "Run context & analytics",
                    description: "Live activity, checkpoints, and fleet KPIs.",
                    actions: html`
                      <div class="fleet-v2-panel__header-actions">
                        ${distractionExitAction} ${resetNodeAction}
                        <button
                          type="button"
                          class="fleet-v2-panel__action"
                          @click=${() =>
                            props.onOpenLocalLlmConsole
                              ? props.onOpenLocalLlmConsole()
                              : props.onNavigateSetup?.("llm-stats")}
                        >
                          Local LLM
                        </button>
                      </div>
                    `,
                  },
                  {
                    id: "inspector",
                    title: "Inspector",
                    description: "Agents, activity, queue, files, and more.",
                    // JAISIU-3082 operator tweak (msg 1a22b312): render
                    // the discrete Inspector width picker inside the
                    // inspector panel header (next to the title), not in
                    // the topbar.
                    actions: html`
                      ${renderInspectorWidthPicker({
                        selectedIndex: props.fleetInspectorWidth ?? 0,
                        onSelect: (idx) => props.onFleetInspectorResize?.(idx),
                        asMenu: true,
                      })}
                    `,
                  },
                ],
                onColumnResize: (index, ratio) => {
                  if (!workspaceLayout || !props.onFleetWorkspaceLayoutChange) {
                    return;
                  }
                  props.onFleetWorkspaceLayoutChange(
                    applyFleetColumnResize(workspacePreset, workspaceLayout, index, ratio),
                  );
                },
                onRowResize: (_index, ratio) => {
                  if (!workspaceLayout || !props.onFleetWorkspaceLayoutChange) {
                    return;
                  }
                  props.onFleetWorkspaceLayoutChange(applyFleetRowResize(workspaceLayout, ratio));
                },
              })
          : nothing
      }
    </div>
  `;

  const sessionChromeCollapsed = props.fleetSessionChromeCollapsed === true;
  const composerCollapsed = props.fleetChatComposerCollapsed === true;
  const sessionNavLayoutClass = sessionNavigatorScrollable
    ? "chat--fleet-session-nav-scrollable"
    : "chat--fleet-session-nav-legacy";

  // JAISIU-3082 operator tweak (msg 9d72e951): lift the chat history
  // sidebar OUT of the chat panel and pin it to the screen's left edge
  // as a flex sibling of the chat-layout column. Renders once for any
  // preset that has a sidebar.
  const chatHistorySidebar = chatHistoryProps
    ? renderFleetChatHistorySidebar(chatHistoryProps)
    : nothing;

  return html`
    <section
      class="card chat chat--fleet ${sessionNavLayoutClass} ${sessionChromeCollapsed ? "chat--fleet-session-collapsed" : ""} ${composerCollapsed ? "chat--fleet-composer-collapsed" : ""} chat--fleet-preset-${workspacePreset}"
    >
      <div class="chat-shell">
        ${chatHistorySidebar}
        <div class="chat-layout">
          <div class="chat-layout__main">
            ${renderFleetPlanActivityBanner(props)} ${renderPinnedActiveJobBanner(props)}
            ${fleetSectionsStack} ${renderComposerReasonChannel(props)}
          </div>
          ${composerInline ? nothing : renderChatStatusStrip({ items: chatStatusItems })}
          ${composerInline ? nothing : renderFleetComposeDock("float")}
        </div>
      </div>
      ${renderChatContextBreakdownPanel({
        open: props.chatContextPanelOpen === true,
        loading: props.chatContextBreakdownLoading === true,
        error: props.chatContextBreakdownError ?? null,
        breakdown: props.chatContextBreakdown ?? null,
        onClose: () => props.onCloseChatContextPanel?.(),
        onRefresh: props.onRefreshChatContextBreakdown,
      })}
    </section>
  `;
}
